/* jk26.js — Fast Candy Julekalender 2026 page behaviours
 * --------------------------------------------------------------------------
 * One namespaced IIFE, no dependencies, no build step. Runs identically in the
 * local preview and inside the Shopify theme (the intro lives in jk26-intro.js).
 * Modules: flags · product state · cart · gallery · snow · erase surface
 *          (frost card + wrapped presents) · carousels · accordion · sticky ·
 *          scroll reveals · preview cart
 * -------------------------------------------------------------------------- */
(function () {
  "use strict";
  if (window.JK26 && window.JK26.booted) return;          // Shopify may include this asset once per section

  const cfg = window.JK26_CONFIG || {
    env: "shopify", intro: { enabled: false }, scratch: { enabled: false, completeAt: .6, brush: 46, sparkles: false },
    characterArt: { enabled: false }, reviews: { enabled: true, mode: "live", children: false }, videos: { enabled: false },
    motion: { intensity: "reduced" }, commerce: { simulateAvailable: false, simulate: "ok", deliveryInfo: "" }, debug: false,
  };
  const html = document.documentElement;
  const $ = (s, r) => (r || document).querySelector(s);
  const $$ = (s, r) => Array.from((r || document).querySelectorAll(s));
  const prefersReduced = !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const motion = prefersReduced ? "off" : (cfg.motion && cfg.motion.intensity) || "full";
  const isPreview = cfg.env === "preview";
  const log = (...a) => { if (cfg.debug) console.debug("[jk26]", ...a); };
  const DPR = Math.min(2, window.devicePixelRatio || 1);

  html.dataset.jk26Env = cfg.env;
  html.dataset.jk26Motion = motion;
  window.JK26 = window.JK26 || {};
  const JK26 = window.JK26;
  JK26.booted = true;
  JK26.config = cfg;

  /* ======================================================================
     FLAGS — every compliance-gated element has exactly one switch
     ====================================================================== */
  function toggleSections(name, on) {
    $$(`[data-jk26-section="${name}"]`).forEach((s) => { s.hidden = !on; });
  }
  function applyFlags() {
    if (!cfg.characterArt || !cfg.characterArt.enabled) $$("[data-jk26-character]").forEach((el) => el.remove());
    toggleSections("reviews", !!(cfg.reviews && cfg.reviews.enabled));
    /* The video sections reach Shopify only when the «Videoer» switch is on. The preview shows exactly that; the
       Lab keeps them on its canvas so they can be built (an empty wall too), marked «Ikke med i Shopify» while
       editing and hidden in «Test siden», which is the customer's view. ?videos=0 / the safe preset hide them. */
    const v = cfg.videos || {};
    const hasVideos = $$('[data-jk26-section="videos"] [data-jk26-video]').length > 0;
    const inLab = /[?&]lab=1/.test(location.search);
    const approved = !!v.enabled;
    toggleSections("videos", inLab ? v.preview !== false : approved && hasVideos && v.preview !== false);
    $$('[data-jk26-section="videos"], [data-jk26-custom="video"]').forEach((s) => s.toggleAttribute("data-jk26-unapproved", !approved));
    if (!inLab && !approved) $$('[data-jk26-custom="video"]').forEach((s) => { s.hidden = true; });
    html.dataset.jk26ReviewsChildren = cfg.reviews && cfg.reviews.children === false ? "0" : "1";
    if (!isPreview) $$("[data-jk26-preview-only]").forEach((el) => el.remove());
    if (!cfg.intro || !cfg.intro.enabled) html.classList.add("jk26-revealed");
  }

  /* ======================================================================
     DESIGN DOCUMENT — sparse overrides from the Julekalender Lab (texts, images,
     layer offsets/scale/width with a phone copy, section order/visibility).
     Preview: injected as window.JK26_DESIGN and re-applied live by the Lab.
     Shopify: baked into the template + jk26-design.css by POST /api/export.
     ====================================================================== */
  const DESIGN_MQ = window.matchMedia ? window.matchMedia("(max-width: 767px)") : null;
  /* desktop values are the base; the phone inherits them and overrides only the props stored in `m` */
  const effectiveOf = (obj, mobile) => (mobile && obj && obj.m ? { ...obj, ...obj.m } : obj || {});
  /* An element on the phone: a place (dx/dy) or width made on the 1440 desktop canvas means nothing in the
     phone's one-column layout (it pushed whole columns off a 390 px screen), so the phone uses only the place
     and width set FOR the phone. Everything else (text, colour, font, rotation, visibility …) still follows
     desktop; an enlargement made on desktop is not blown up on the phone, and a desktop font size is capped
     to the screen in jk26.css. The export writes the same rules (server.mjs → phoneLayerDecl). */
  const PHONE_OWN = ["dx", "dy", "w"];
  function layerFor(rec, mobile) {
    if (!mobile || !rec) return effectiveOf(rec, mobile);
    const m = rec.m || {}, own = (k) => m[k] !== undefined && m[k] !== null;
    const out = { ...rec, ...m };
    for (const k of PHONE_OWN) out[k] = own(k) ? m[k] : k === "w" ? null : 0;
    if (!own("scale") && rec.scale > 1) out.scale = 1;
    out.fsOwn = own("fs");
    return out;
  }
  function setVar(el, attr, cssVar, value, unit) {
    if (value === null || value === undefined || value === "" || value === false) { el.style.removeProperty(cssVar); el.removeAttribute(attr); }
    else { el.style.setProperty(cssVar, unit ? value + unit : String(value)); el.setAttribute(attr, unit === null ? String(value) : "1"); }
  }
  /* one styler for ordinary layers AND decorative PNGs (position, size, typography, rotation, opacity, animation) */
  function applyLayerVars(el, v) {
    const s = el.style;
    v.dx ? s.setProperty("--jk26-dx", v.dx + "px") : s.removeProperty("--jk26-dx");
    v.dy ? s.setProperty("--jk26-dy", v.dy + "px") : s.removeProperty("--jk26-dy");
    v.scale && v.scale !== 1 ? s.setProperty("--jk26-scale", String(v.scale)) : s.removeProperty("--jk26-scale");
    setVar(el, "data-jk26-w", "--jk26-w", v.w || null, "px");
    setVar(el, "data-jk26-fs", "--jk26-fs", v.fs || null, "px");
    el.toggleAttribute("data-jk26-fs-own", !!(v.fs && v.fsOwn));
    setVar(el, "data-jk26-fw", "--jk26-fw", v.fw || null);
    setVar(el, "data-jk26-align", "--jk26-align", v.align || null);
    setVar(el, "data-jk26-ls", "--jk26-ls", v.ls === null || v.ls === undefined || v.ls === 0 ? null : v.ls, "em");
    setVar(el, "data-jk26-lh", "--jk26-lh", v.lh || null);
    setVar(el, "data-jk26-color", "--jk26-color", v.color || null);
    setVar(el, "data-jk26-rot", "--jk26-rot", v.rot ? v.rot : null, "deg");
    setVar(el, "data-jk26-z", "--jk26-z", v.z === null || v.z === undefined ? null : v.z, "");
    setVar(el, "data-jk26-op", "--jk26-op", v.op === null || v.op === undefined || v.op >= 100 ? null : Math.max(0, v.op) / 100, "");
    setVar(el, "data-jk26-mt", "--jk26-mt", v.mt === null || v.mt === undefined ? null : v.mt, "px");
    setVar(el, "data-jk26-mb", "--jk26-mb", v.mb === null || v.mb === undefined ? null : v.mb, "px");
    setVar(el, "data-jk26-ff", "--jk26-ff-x", v.ff || null, null);
    /* «Pakker og kjøpsknapp»: text sizes in % → multipliers for jk26.css (1 = standard; the export writes the same) */
    [["bbT", "t"], ["bbN", "n"], ["bbS", "s"], ["bbP", "p"], ["bbB", "b"]].forEach(([k, c]) => { const n = Number(v[k]); n > 0 && n !== 100 ? s.setProperty("--jk26-bb-" + c, String(n / 100)) : s.removeProperty("--jk26-bb-" + c); });
    const an = v.an && v.an.t && v.an.t !== "none" ? v.an : null;
    if (an) {
      el.setAttribute("data-jk26-an", an.t);
      el.setAttribute("data-jk26-adir", an.dir === "y" ? "y" : "x");
      s.setProperty("--jk26-asp", (an.sp || 6) + "s");
      s.setProperty("--jk26-aam", (an.am || 12) + "px");
      s.setProperty("--jk26-aamr", Math.min(45, an.am || 6) + "deg");
      s.setProperty("--jk26-adl", (an.dl || 0) + "s");
    } else { el.removeAttribute("data-jk26-an"); el.removeAttribute("data-jk26-adir"); ["--jk26-asp", "--jk26-aam", "--jk26-aamr", "--jk26-adl"].forEach((p) => s.removeProperty(p)); }
    el.toggleAttribute("data-jk26-hidden", !!v.hidden);
  }
  /* The part of a picture that stays visible in its frame (review photos): pan with object-position,
     zoom with a scale whose origin is the same point, so the frame is always filled and only the crop
     moves. Stored next to the picture override: design.images[key] = { px, py (0–100 %), pz (1–4) }. */
  function applyImageCrop(img, v) {
    const has = v && (v.px !== undefined && v.px !== null || v.py !== undefined && v.py !== null || (v.pz && v.pz !== 1));
    if (!has) { img.style.removeProperty("object-position"); img.style.removeProperty("transform"); img.style.removeProperty("transform-origin"); return; }
    const px = Math.max(0, Math.min(100, v.px === undefined || v.px === null ? 50 : v.px));
    const py = Math.max(0, Math.min(100, v.py === undefined || v.py === null ? 50 : v.py));
    const pz = Math.max(1, Math.min(4, v.pz || 1));
    img.style.objectPosition = `${px}% ${py}%`;
    if (pz > 1) { img.style.transform = `scale(${pz})`; img.style.transformOrigin = `${px}% ${py}%`; }
    else { img.style.removeProperty("transform"); img.style.removeProperty("transform-origin"); }
  }

  /* theme assets (Shopify) carry bare file names; the preview carries site-relative paths */
  function resolveImageUrl(src) {
    if (!src || /\//.test(src)) return src;
    const link = document.querySelector('link[href*="jk26.css"]');
    return link ? link.href.replace(/jk26\.css.*$/, src) : src;
  }
  /* decorative PNGs: created/updated/removed from the design document (preview) or window.JK26_DECORS (Shopify) */
  /* ---- where a PNG sits in its section ---------------------------------------
   * Stored (design.decors[id]): dx, dy, w in px of the design canvas (1440 wide; 390 when the phone has a
   * placement of its own in .m), scale, sh = the section's height when it was placed, ax = optional anchor.
   * Shown: fractions of the design frame sideways, a relative place between the section's top and bottom
   * edge up/down (see the .jk26-decor rules in jk26.css). All of it is measured on the VISIBLE box: the
   * scale works around the centre, so a PNG scaled up reaches further than its layout box. */
  const DECOR_REF = { desktop: 1440, phone: 390 };
  /* by default a PNG stays with the content, so the composition made at 1440 is kept whole on a wider screen;
     pinning it to the section's left or right edge is a choice made in the Lab ("Følger") */
  const decorAnchor = (rec) => (rec.ax === "l" || rec.ax === "r" ? rec.ax : "c");
  function decorGeometry(rec, mobile) {
    const has = (o, k) => o && o[k] !== undefined && o[k] !== null;
    const own = mobile && rec.m && ["dx", "dy", "w"].some((k) => has(rec.m, k)) ? rec.m : null;   // the phone has its own placement
    const toPhone = own ? DECOR_REF.phone / DECOR_REF.desktop : 1;   // an older phone override may hold only some of the numbers
    const pick = (k, d) => (has(own, k) ? own[k] : has(rec, k) ? (k === "dy" ? rec[k] : rec[k] * toPhone) : d);
    const eff = effectiveOf(rec, mobile);
    return { ref: own ? DECOR_REF.phone : DECOR_REF.desktop, dx: pick("dx", 0), dy: pick("dy", 0), w: pick("w", 160), scale: eff.scale || 1, sh: own ? own.sh || null : rec.sh || null, ax: decorAnchor(rec) };
  }
  function placeDecor(el) {
    const g = el._jk26geo; if (!g) return;
    const img = el.firstChild, st = el.style;
    const ratio = img && img.naturalWidth ? img.naturalHeight / img.naturalWidth : 0;
    /* DRAWN AT ITS VISIBLE SIZE: the box is the stored width x the zoom, never a small box magnified with CSS
       scale. A magnified box is rasterised at its small size and then stretched, so the picture went blurry (a
       phone PNG of 49 px x 7.19 showed a 49 px bitmap at 352 px). Same place, same centre as before (2026-10-15). */
    const s = g.scale || 1, vx = g.dx - (g.w * (s - 1)) / 2;
    st.setProperty("--jk26-fx", String(vx / g.ref));
    st.setProperty("--jk26-fw", String((g.w * s) / g.ref));
    st.setProperty("--jk26-dk", String(s));   // the float/drift distances were zoomed with the box: they still are
    el.setAttribute("data-jk26-ax", g.ax);
    let top = g.dy + "px", tyFactor = (1 - s) / (2 * s);   // not placed against a known section height yet: plain distance from the top, as before
    if (g.sh && ratio) {
      const eh = g.w * ratio, vh = eh * s, vTop = g.dy + (eh * (1 - s)) / 2;   // the visible box at design time
      if (vh < g.sh * 0.85) {   // 0 = touching the section's top edge, 1 = touching its bottom edge
        const pos = vTop / (g.sh - vh);
        top = (pos * 100).toFixed(4) + "%"; tyFactor = -pos;
      } else {                  // about as tall as the section: its centre keeps its relative height
        top = (((vTop + vh / 2) / g.sh) * 100).toFixed(4) + "%"; tyFactor = -0.5;
      }
    }
    st.setProperty("--jk26-top", top);
    st.setProperty("--jk26-ty", (tyFactor * 100).toFixed(4) + "%");
    g.tyFactor = tyFactor;
    el.removeAttribute("data-jk26-raw");
    /* a phone is one column: a PNG placed on desktop stays as a small accent (up to about a third of the
       screen), a bigger one would cover the text, so it waits until it is placed FOR the phone (the Lab shows
       it faded there so it can be picked up) */
    const S = el.parentElement ? el.parentElement.clientWidth : 0;
    const big = g.ref === DECOR_REF.desktop && !!(DESIGN_MQ && DESIGN_MQ.matches) && S > 0 && (g.w / g.ref) * Math.min(1440, S) * g.scale > S * 0.35;
    el.toggleAttribute("data-jk26-phone-hidden", big);
  }
  function applyDecors(decors, mobile) {
    const wanted = decors || {};
    $$(".jk26-decor").forEach((el) => { if (!wanted[el.dataset.jk26Decor]) el.remove(); });
    for (const [id, rec] of Object.entries(wanted)) {
      if (!rec || !rec.src) continue;
      const sec = $(`[data-jk26-section-key="${CSS.escape(rec.section || "hero")}"]`);
      if (!sec) continue;
      let el = $(`.jk26-decor[data-jk26-decor="${CSS.escape(id)}"]`);
      if (!el) {
        el = document.createElement("div");
        el.className = "jk26-decor";
        el.dataset.jk26Decor = id;
        el.dataset.jk26Layer = "decor." + id;
        el.dataset.jk26Kind = "decor";
        el.setAttribute("aria-hidden", "true");
        el.appendChild(document.createElement("img"));
        el.firstChild.alt = "";
        el.firstChild.decoding = "async";
      }
      if (el.parentElement !== sec) sec.appendChild(el);
      el.dataset.jk26Label = rec.name || "PNG-element";
      const img = el.firstChild;
      const url = resolveImageUrl(rec.src);
      if (img.getAttribute("src") !== url) img.src = url;
      /* rotation, opacity, layer order, animation, visibility as for any element; the PLACE is set by placeDecor */
      applyLayerVars(el, { ...effectiveOf(rec, mobile), dx: 0, dy: 0, w: null });
      el._jk26geo = decorGeometry(rec, mobile);
      placeDecor(el);
      if (!img.complete || !img.naturalWidth) img.addEventListener("load", () => placeDecor(el), { once: true });   // the up/down place needs the picture's proportions
    }
  }
  function applyDesign(d) {
    d = d || {};
    const mobile = !!(DESIGN_MQ && DESIGN_MQ.matches);
    html.dataset.jk26Devnotes = d.flags && d.flags.devNotes === false ? "0" : "1";
    const main = $("[data-jk26-main]");
    if (main) {
      const nodes = $$("[data-jk26-section-key]", main);
      const byKey = new Map(nodes.map((n) => [n.dataset.jk26SectionKey, n]));
      nodes.forEach((n) => {
        const S = effectiveOf(d.sections && d.sections[n.dataset.jk26SectionKey], mobile);
        n.toggleAttribute("data-jk26-design-hidden", !!S.hidden);
        let bg = S.bg || null;
        if (bg && S.bgA !== null && S.bgA !== undefined && S.bgA < 100 && /^#[0-9a-f]{6}$/i.test(bg)) bg += Math.round((S.bgA / 100) * 255).toString(16).padStart(2, "0");
        setVar(n, "data-jk26-bg", "--jk26-sec-bg", bg);
        /* the phone's own picture, else the desktop one (an emptied phone picture never hides the desktop one) */
        const R = (d.sections && d.sections[n.dataset.jk26SectionKey]) || {};
        const pic = (mobile && R.m && R.m.bgImage) || R.bgImage || null;
        setVar(n, "data-jk26-bgimg", "--jk26-sec-bgimg", pic ? `url("${resolveImageUrl(pic)}")` : null);
        const fx = typeof S.bgX === "number" ? S.bgX : 50, fy = typeof S.bgY === "number" ? S.bgY : 50;
        setVar(n, "data-jk26-bgpos", "--jk26-sec-bgpos", fx === 50 && fy === 50 ? null : `${fx}% ${fy}%`);
        setVar(n, "data-jk26-bgop", "--jk26-sec-bgop", S.bgOp === null || S.bgOp === undefined || S.bgOp >= 100 ? null : Math.max(0, S.bgOp) / 100, "");
        setVar(n, "data-jk26-pt", "--jk26-sec-pt", S.pt === null || S.pt === undefined ? null : S.pt, "px");
        setVar(n, "data-jk26-pb", "--jk26-sec-pb", S.pb === null || S.pb === undefined ? null : S.pb, "px");
        const Sm = (d.sections && d.sections[n.dataset.jk26SectionKey] && d.sections[n.dataset.jk26SectionKey].m) || {};
        n.toggleAttribute("data-jk26-pt-own", Sm.pt !== undefined && Sm.pt !== null);   // jk26.css scales inherited desktop air on a phone
        n.toggleAttribute("data-jk26-pb-own", Sm.pb !== undefined && Sm.pb !== null);
        setVar(n, "data-jk26-minh", "--jk26-sec-minh", S.minH === null || S.minH === undefined ? null : S.minH, "px");
        /* an exact height is the device's own: a desktop one never reaches the phone (one column, far taller content) */
        const hOwn = mobile ? (R.m && typeof R.m.h === "number" ? R.m.h : null) : typeof R.h === "number" ? R.h : null;
        setVar(n, "data-jk26-h", "--jk26-sec-h", hOwn, "px");
        n.toggleAttribute("data-jk26-nopreorder", S.preorder === false);
        if (n.dataset.jk26SectionKey === "hero") html.dataset.jk26StickyPreorder = S.preorder === false ? "0" : "1";   // the fixed buy bar follows the hero
        n.toggleAttribute("data-jk26-nosnow", S.snow === false);
        n.toggleAttribute("data-jk26-nolights", S.lights === false);
        setVar(n, "data-jk26-mh", "--jk26-marquee-h", S.logoH || null, "px");
        setVar(n, "data-jk26-ms", "--jk26-marquee-speed", S.speed || null, "s");
        setVar(n, "data-jk26-mbg", "--jk26-marquee-bg", S.color || null);
      });
      if (Array.isArray(d.order) && d.order.length) {
        const footer = $(".jk26-pvfooter", main);
        const ordered = d.order.map((k) => byKey.get(k)).filter(Boolean);
        for (const n of nodes) if (!ordered.includes(n)) ordered.push(n);
        ordered.forEach((n) => main.insertBefore(n, footer || null));
      }
    }
    if (JK26.presents && d.presents && d.presents.order) JK26.presents.setOrder(d.presents.order);
    if (JK26.gallery) JK26.gallery.set(d.gallery);
    const safeCopy = !!(cfg.copy && cfg.copy.collectible === false);   // the safe preset: wording flagged in the compliance review is left out
    $$("[data-jk26-edit]").forEach((el) => {
      const k = el.dataset.jk26Edit;
      if (el.dataset.jk26DefaultHtml === undefined) { el.dataset.jk26DefaultHtml = el.innerHTML; el.dataset.jk26DefaultText = el.textContent.trim(); }
      const v = d.texts && d.texts[k];
      if (v !== undefined && v !== null) { if (el.textContent !== v) el.textContent = v; }
      else if (safeCopy && el.dataset.jk26SafeText) { if (el.textContent !== el.dataset.jk26SafeText) el.textContent = el.dataset.jk26SafeText; }
      else if (el.innerHTML !== el.dataset.jk26DefaultHtml) el.innerHTML = el.dataset.jk26DefaultHtml;
    });
    /* the words in front of every package countdown: one text for all rows (the Lab's «Tekst foran nedtellingen»);
       the attribute holds the standard words the Lab returns to when its text is emptied */
    $$("[data-jk26-timer-label]").forEach((el) => {
      const v = d.texts && typeof d.texts["offers.timer"] === "string" && d.texts["offers.timer"].trim();
      const t = v || el.dataset.jk26TimerLabel || el.textContent;
      if (el.textContent !== t) el.textContent = t;
    });
    /* the folds under the buy box: which start open is the merchant's choice (design.folds, Lab → hero);
       on the shop a fold the visitor opened or closed keeps the visitor's choice */
    const inLab = html.classList.contains("jk26-lab");
    $$("[data-jk26-fold]").forEach((f) => {
      if (f.dataset.jk26Touched === "1" && !inLab) return;
      const k = f.dataset.jk26Fold;
      const want = d.folds && typeof d.folds[k] === "boolean" ? d.folds[k] : f.hasAttribute("data-jk26-fold-open");
      f.classList.toggle("is-open", want);
      const b = $(".jk26-acc__btn", f); if (b) b.setAttribute("aria-expanded", want ? "true" : "false");
    });
    /* an e-mail address in an answer stays a link, also after the text was edited in the Lab (Shopify's rich text already links it) */
    $$("[data-jk26-linkify]").forEach((el) => {
      if (el.querySelector("a") || el.isContentEditable) return;
      const parts = el.textContent.split(/([\w.+-]+@[\w-]+(?:\.[\w-]+)+)/);
      if (parts.length < 2) return;
      el.textContent = "";
      parts.forEach((p, i) => {
        if (i % 2) { const a = document.createElement("a"); a.className = "jk26-inline-link"; a.href = "mailto:" + p; a.textContent = p; el.appendChild(a); }
        else if (p) el.appendChild(document.createTextNode(p));
      });
    });
    $$("[data-jk26-img]").forEach((el) => {
      const v = d.images && d.images[el.dataset.jk26Img];
      if (el.tagName === "IMG") {
        if (el.dataset.jk26DefaultSrc === undefined) { el.dataset.jk26DefaultSrc = el.getAttribute("src") || ""; el.dataset.jk26DefaultSrcset = el.getAttribute("srcset") || ""; }
        if (v && v.src) { if (el.getAttribute("src") !== v.src) { el.removeAttribute("srcset"); el.src = v.src; } }
        else if (el.getAttribute("src") !== el.dataset.jk26DefaultSrc) { el.src = el.dataset.jk26DefaultSrc; if (el.dataset.jk26DefaultSrcset) el.setAttribute("srcset", el.dataset.jk26DefaultSrcset); }
      } else { /* a slot (e.g. a review photo): Lab override, else the slot's own default picture, else empty */
        const src = (v && v.src) || el.dataset.jk26DefaultSrc || "";
        let img = el.querySelector("img[data-jk26-slot-img]");
        if (src) { if (!img) { img = document.createElement("img"); img.dataset.jk26SlotImg = "1"; img.alt = ""; el.insertBefore(img, el.firstChild); } if (img.getAttribute("src") !== src) img.src = src; el.classList.add("has-image"); }
        else { if (img) img.remove(); el.classList.remove("has-image"); }
        if (img && img.isConnected) applyImageCrop(img, v);
      }
    });
    syncRatingLines();
    $$("[data-jk26-layer]").forEach((el) => {
      if (el.classList.contains("jk26-decor")) return;                     // styled by applyDecors
      applyLayerVars(el, layerFor(d.layers && d.layers[el.dataset.jk26Layer], mobile));
    });
    applyDecors(d.decors && Object.keys(d.decors).length ? d.decors : window.JK26_DECORS, mobile);
    document.dispatchEvent(new CustomEvent("jk26:design-applied"));
  }
  /* the star fill follows the number written in the line ("4,4 av 5 …"), so editing the text keeps them in step */
  function syncRatingLines() {
    $$("[data-jk26-ratingline]").forEach((p) => {
      const stars = $(".jk26-ratingline__stars", p); if (!stars) return;
      const m = p.textContent.match(/(\d+(?:[.,]\d+)?)\s*(?:av|\/)\s*5/);
      if (m) stars.style.setProperty("--r", String(Math.max(0, Math.min(5, parseFloat(m[1].replace(",", "."))))));
    });
  }
  JK26.design = { current: window.JK26_DESIGN || null, apply(d) { JK26.design.current = d; applyDesign(d); } };
  /* the preview and the Lab apply the whole design document; on Shopify the design ships as the template +
     jk26-design.css, so only the PNGs (window.JK26_DECORS) are placed by script — at load and at the phone breakpoint */
  const applyCurrent = () => {
    if (JK26.design.current) applyDesign(JK26.design.current);
    else applyDecors(window.JK26_DECORS, !!(DESIGN_MQ && DESIGN_MQ.matches));
  };
  function initDesign() {
    syncRatingLines();
    applyCurrent();
    if (DESIGN_MQ && DESIGN_MQ.addEventListener) DESIGN_MQ.addEventListener("change", applyCurrent);
  }

  /* ======================================================================
     PRODUCT STATE
     ====================================================================== */
  /* cta: what the buy button says when it cannot be used (null = it keeps its own text: the Lab's / the theme setting's) */
  const LABELS = {
    available: { chip: "På lager", cta: null },
    preorder: { chip: "Forhåndsbestilling", cta: null },
    soldout: { chip: "Utsolgt", cta: "Utsolgt" },
    unavailable: { chip: "Ikke tilgjengelig", cta: "Ikke tilgjengelig" },
  };
  const product = { state: "preorder", variantId: null, price: null };
  /* with packages on the page the button can only be used while at least one package can be bought */
  const offersOpen = () => { const r = $("[data-jk26-offers]"); return !r || !!$(".jk26-bundle:not(.is-off)", r); };

  function setAvailability(state) {
    product.state = LABELS[state] ? state : "unavailable";
    const l = LABELS[product.state];
    const chip = $("[data-jk26-availability]");
    if (chip) { chip.dataset.state = product.state; chip.textContent = l.chip; }
    $$("[data-jk26-availability-text]").forEach((el) => { el.textContent = l.chip; el.dataset.state = product.state; });   // the state styles the pre-order pill and its glow
    const sellable = product.state === "available" || product.state === "preorder";
    const purchasable = sellable && offersOpen();
    const why = !sellable ? l.cta : purchasable ? null : "Utsolgt";
    $$("[data-jk26-add], [data-jk26-goto-offers]").forEach((btn) => {
      if (btn.hasAttribute("data-jk26-add")) { btn.classList.toggle("is-disabled", !purchasable); btn.setAttribute("aria-disabled", purchasable ? "false" : "true"); }
      const lab = $("[data-jk26-add-label], .jk26-cta__label", btn); if (!lab) return;
      if (why) { if (lab.dataset.jk26Ready === undefined) lab.dataset.jk26Ready = lab.textContent; lab.textContent = why; }
      else if (lab.dataset.jk26Ready !== undefined) { lab.textContent = lab.dataset.jk26Ready; delete lab.dataset.jk26Ready; }
    });
  }

  function initProduct() {
    const idInput = $("[data-jk26-variant-id]");
    product.variantId = idInput ? Number(idInput.value) : null;
    const delivery = $("[data-jk26-delivery]");
    if (delivery && cfg.commerce && cfg.commerce.deliveryInfo) { delivery.textContent = cfg.commerce.deliveryInfo; delivery.hidden = false; const ph = $(".jk26-buy__placeholder"); if (ph) ph.remove(); }
    if (isPreview) {
      setAvailability(cfg.commerce && cfg.commerce.simulateAvailable ? "preorder" : "soldout");
      fetch("api/product", { headers: { Accept: "application/json" } }).then((r) => r.json()).then((snap) => {
        const v = snap.product.variants[0];
        product.price = v.price_cents;
        if (!cfg.commerce.simulateAvailable) setAvailability(v.available_for_sale ? (snap.product.title.toLowerCase().includes("forh") ? "preorder" : "available") : "soldout");
        log("product snapshot", snap._about.captured_at, snap.product.status);
      }).catch(() => log("snapshot unavailable"));
    } else {
      const chip = $("[data-jk26-availability]");
      setAvailability(chip && chip.dataset.state ? chip.dataset.state : "available");
    }
  }

  /* ======================================================================
     CART — Shopify Ajax API shape on both hosts
     ====================================================================== */
  function routes() {
    const r = (window.FoxTheme && window.FoxTheme.routes) || {};
    const add = r.cart_add_url || "/cart/add";
    return { add: add.endsWith(".js") ? add : add + ".js", cart: (r.cart_url || "/cart").replace(/\.js$/, "") + ".js" };
  }
  /* Shopify's money formats as Liquid's `money` filter renders them (server copy: tools/money.mjs). The store's
     format comes from the page (preview: the bundle facts; Shopify: shop.money_format via the hero's config). */
  const MONEY_STYLES = { amount: [2, ",", "."], amount_no_decimals: [0, ",", "."], amount_with_comma_separator: [2, ".", ","], amount_no_decimals_with_comma_separator: [0, ".", ","], amount_with_apostrophe_separator: [2, "'", "."], amount_no_decimals_with_space_separator: [0, " ", ""], amount_with_space_separator: [2, " ", ","], amount_with_period_and_space_separator: [2, " ", "."] };
  function formatMoney(cents) {
    const fmt = cfg.moneyFormat || (window.FoxTheme && window.FoxTheme.settings && window.FoxTheme.settings.moneyFormat) || "{{amount_no_decimals}} kr";
    return String(fmt).replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
      const [d, th, dec] = MONEY_STYLES[key] || MONEY_STYLES.amount;
      const [whole, frac] = (Math.round(cents) / 100).toFixed(d).split(".");
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (frac ? dec + frac : "");
    }).replace(/<[^>]+>/g, "");
  }
  JK26.formatMoney = formatMoney;

  /* one variant, or a whole bundle: addToCart(id, qty) and addToCart([{ id, quantity }, …]) both work */
  async function addToCart(variantId, quantity) {
    const sections = [];
    try { html.dispatchEvent(new CustomEvent("cart:grouped-sections", { bubbles: true, detail: { sections } })); } catch { /* ignore */ }
    const items = Array.isArray(variantId) ? variantId : [{ id: variantId, quantity: quantity || 1 }];
    const body = { items };
    if (sections.length) { body.sections = sections.join(","); body.sections_url = location.pathname; }
    const headers = { "Content-Type": "application/json", Accept: "application/json", "X-Requested-With": "XMLHttpRequest" };
    if (isPreview && cfg.commerce && cfg.commerce.simulate && cfg.commerce.simulate !== "ok") headers["X-JK26-Simulate"] = cfg.commerce.simulate;
    const rt = routes();
    const res = await fetch(rt.add, { method: "POST", headers, body: JSON.stringify(body), credentials: "same-origin" });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.status) { const e = new Error(json.description || json.message || "Kunne ikke legge varen i handlekurven."); e.cart = true; throw e; }
    const cart = await fetch(rt.cart, { headers: { Accept: "application/json" }, credentials: "same-origin" }).then((r) => r.json()).catch(() => null);
    if (cart) cart.sections = json.sections;
    try { /* hand the result to the theme so its cart drawer re-renders and opens */
      if (window.FoxTheme && window.FoxTheme.pubsub && cart) window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: "jk26", cart, productVariantId: items[0].id });
      const drawer = document.querySelector("cart-drawer");
      if (drawer) { if (typeof drawer.open === "function") drawer.open(); else if (typeof drawer.show === "function") drawer.show(); }
    } catch (err) { log("theme cart hook failed", err); }
    document.dispatchEvent(new CustomEvent("jk26:cart-added", { detail: { item: json, cart } }));
    return { item: json, cart };
  }

  let toastTimer = 0;
  function toast(msg, isError) {
    let el = $(".jk26-toast");
    if (!el) { el = document.createElement("div"); el.className = "jk26-toast"; el.setAttribute("role", "status"); document.body.appendChild(el); }
    el.textContent = msg;
    el.classList.toggle("jk26-toast--error", !!isError);
    requestAnimationFrame(() => el.classList.add("is-visible"));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove("is-visible"), isError ? 4200 : 2600);
  }

  /* ---- every buy button below the hero («Legg i handlekurven», like the hero's) leads to the package choice ----
     A smooth scroll up to the packages (#jk26-velg-pakke), no address or history change, with room for the shop's
     sticky header (it slides back in while the page scrolls up). Only the hero's own button adds to the cart. */
  function headerRoom() {
    let h = 0;
    for (const x of [8, innerWidth / 2, innerWidth - 8]) for (const el of document.elementsFromPoint(x, 1)) {
      if (el.closest(".jk26-sticky, .jk26-toast, .jk26-intro")) continue;
      for (let n = el; n && n !== document.body && n !== html; n = n.parentElement) {
        const p = getComputedStyle(n).position;
        if (p === "fixed" || p === "sticky") { const r = n.getBoundingClientRect(); if (r.top <= 1 && r.height < innerHeight / 3) h = Math.max(h, r.bottom); break; }
      }
    }
    const theme = parseFloat(getComputedStyle(html).getPropertyValue("--header-height"));   // the store theme's header, also while it is tucked away
    if (theme > 0 && theme < innerHeight / 3) h = Math.max(h, theme);
    const pv = $(".jk26-pvheader"); if (pv && getComputedStyle(pv).position === "sticky") h = Math.max(h, pv.offsetHeight);   // the preview's own header
    return h;
  }
  function scrollToOffers() {
    const target = document.getElementById("jk26-velg-pakke") || $("[data-jk26-buy]");
    if (!target) return false;
    const y = Math.max(0, target.getBoundingClientRect().top + scrollY - headerRoom() - 14);
    const smooth = motion !== "off" && !(window.matchMedia && matchMedia("(prefers-reduced-motion: reduce)").matches);
    window.scrollTo({ top: y, behavior: smooth ? "smooth" : "auto" });
    /* keyboard and screen-reader users land on the picked package (focus moves without a second scroll) */
    const radio = $(".jk26-bundle__radio:checked", target) || $(".jk26-bundle__radio:not(:disabled)", target);
    const land = () => { try { if (radio) radio.focus({ preventScroll: true }); } catch { /* ignore */ } };
    if (!smooth) land();
    else { let t = 0; const done = () => { clearTimeout(t); removeEventListener("scrollend", done); land(); }; addEventListener("scrollend", done); t = setTimeout(done, 1000); }
    return true;
  }
  JK26.scrollToOffers = scrollToOffers;

  let busy = false, afterglow = 0;
  function initBuy() {
    const form = $("[data-jk26-buy]");
    const msg = $("[data-jk26-buy-msg]");
    const buttons = $$("[data-jk26-add]");                 // the hero's buy button (the only one that adds)
    const say = (text, isError, sourceBtn) => {
      if (msg && (!sourceBtn || (form && form.contains(sourceBtn)))) { msg.textContent = text; msg.classList.toggle("is-error", !!isError); }
      else toast(text.replace(/\.$/, ""), isError);
    };
    async function run(sourceBtn) {
      if (busy) return;                                      // one press = one add: nothing is sent twice while a request runs
      if (!(product.state === "available" || product.state === "preorder")) { toast(LABELS[product.state].chip, true); return; }
      let items;
      const offersRoot = $("[data-jk26-offers]");
      if (offersRoot) {
        /* with packages on the page only a package that can be bought goes into the cart: never a lone calendar as a
           fallback, never a package marked sold out (checked here again, not only by the disabled radio), never one
           whose countdown ran out a moment ago (sweep) */
        if (JK26.bundles && JK26.bundles.sweep) JK26.bundles.sweep();
        const row = bundle.id ? $(`.jk26-bundle[data-jk26-offer="${CSS.escape(bundle.id)}"]`, offersRoot) : null;
        if (!row || row.classList.contains("is-off") || !bundle.items || !bundle.items.length) {
          say(offersOpen() ? "Velg en pakke som er tilgjengelig." : "Alle pakkene er utsolgt akkurat nå.", true, sourceBtn);
          return;
        }
        items = bundle.items;
      } else items = [{ id: product.variantId, quantity: 1 }];
      if (!items[0].id) { toast("Produktet er ikke konfigurert (variant mangler).", true); return; }
      const many = items.length > 1 || items[0].quantity > 1;
      busy = true;
      clearTimeout(afterglow);
      const labels = buttons.map((b) => $("[data-jk26-add-label]", b));
      const ready = labels.map((l) => (l ? (l.dataset.jk26Before !== undefined ? l.dataset.jk26Before : l.textContent) : ""));
      labels.forEach((l, i) => { if (l) l.dataset.jk26Before = ready[i]; });
      const restore = () => labels.forEach((l, i) => { if (l) { l.textContent = ready[i]; delete l.dataset.jk26Before; } });
      buttons.forEach((b, i) => { b.classList.remove("is-success"); b.classList.add("is-loading"); b.setAttribute("aria-disabled", "true"); b.setAttribute("aria-busy", "true"); if (labels[i]) labels[i].textContent = "Legger til …"; });
      if (msg) { msg.textContent = ""; msg.classList.remove("is-error"); }
      try {
        await addToCart(items);
        buttons.forEach((b, i) => { b.classList.remove("is-loading"); b.classList.add("is-success"); b.removeAttribute("aria-busy"); b.setAttribute("aria-disabled", "false"); if (labels[i]) labels[i].textContent = "Lagt i handlekurven"; });
        /* how many times this package has gone into the cart (the success look above is only shown briefly) */
        const row = bundle.id ? $(`.jk26-bundle[data-jk26-offer="${CSS.escape(bundle.id)}"]`) : null;
        for (const el of [row, form].filter(Boolean)) el.dataset.jk26Added = String((Number(el.dataset.jk26Added) || 0) + 1);
        const n = bundle.qty || 1;
        say(n > 1 ? `${n} pakker er lagt i handlekurven.` : many ? "Pakken er lagt i handlekurven." : "Varen er lagt i handlekurven.", false, sourceBtn);
        afterglow = setTimeout(() => { buttons.forEach((b) => b.classList.remove("is-success")); restore(); if (msg) msg.textContent = ""; }, 2400);
      } catch (err) {
        buttons.forEach((b) => { b.classList.remove("is-loading"); b.removeAttribute("aria-busy"); b.setAttribute("aria-disabled", "false"); });
        restore();
        say(err && err.cart ? err.message : "Noe gikk galt. Sjekk nettforbindelsen og prøv igjen.", true, sourceBtn);
        log("add failed", err);
      } finally { busy = false; }
    }
    if (form) form.addEventListener("submit", (e) => { e.preventDefault(); run($("[data-jk26-add]", form)); });
    /* the buttons below the hero (and in the fixed bar) never add: they lead to the package choice */
    document.addEventListener("click", (e) => {
      const go = e.target.closest("[data-jk26-goto-offers]");
      if (!go) return;
      e.preventDefault();
      scrollToOffers();
    });
    JK26.addToCart = () => run(null);
  }

  /* ======================================================================
     GALLERY (hero)
     ====================================================================== */
  function initGallery() {
    const root = $("[data-jk26-gallery]");
    if (!root) return;
    /* The pictures can be replaced at any time (the merchant's own gallery from the Lab), so nothing here
       holds on to the image or thumbnail elements: every lookup is live and the listeners are delegated. */
    const vp = $(".jk26-gallery__viewport", root);
    const thumbsBox = $(".jk26-thumbs", root.closest("[data-jk26-section-key]") || document);
    const imgs = () => $$("[data-jk26-gallery-img]", root);
    const thumbs = () => (thumbsBox ? $$("[data-jk26-thumb]", thumbsBox) : []);
    let idx = 0;
    function show(i, focusThumb) {
      const list = imgs(); if (!list.length) return;
      idx = ((i % list.length) + list.length) % list.length;
      list.forEach((im, k) => im.classList.toggle("is-active", k === idx));
      const ts = thumbs();
      ts.forEach((t, k) => { t.classList.toggle("is-active", k === idx); t.setAttribute("aria-selected", k === idx ? "true" : "false"); t.tabIndex = k === idx ? 0 : -1; });
      root.dataset.count = String(list.length);
      const cur = ts[idx];
      if (cur && thumbsBox && thumbsBox.scrollWidth > thumbsBox.clientWidth) thumbsBox.scrollTo({ left: Math.max(0, cur.offsetLeft - (thumbsBox.clientWidth - cur.offsetWidth) / 2), behavior: motion === "off" ? "auto" : "smooth" });   // never scrollIntoView: that would move the page
      if (cur && focusThumb) cur.focus();
    }
    if (thumbsBox) {
      thumbsBox.addEventListener("click", (e) => { const t = e.target.closest("[data-jk26-thumb]"); if (t) show(thumbs().indexOf(t)); });
      thumbsBox.addEventListener("keydown", (e) => {
        if (!e.target.closest("[data-jk26-thumb]")) return;
        if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); show(idx + 1, true); }
        if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); show(idx - 1, true); }
      });
    }
    const prev = $("[data-jk26-gallery-prev]", root), next = $("[data-jk26-gallery-next]", root);
    prev && prev.addEventListener("click", () => show(idx - 1));
    next && next.addEventListener("click", () => show(idx + 1));
    let sx = null, sy = null;
    vp.addEventListener("dragstart", (e) => e.preventDefault());   // with a mouse, dragging a picture would start the browser's own image drag and the swipe would never finish
    vp.addEventListener("pointerdown", (e) => { sx = e.clientX; sy = e.clientY; }, { passive: true });
    vp.addEventListener("pointerup", (e) => {
      if (sx === null) return;
      const dx = e.clientX - sx, dy = e.clientY - sy; sx = sy = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.4) show(dx < 0 ? idx + 1 : idx - 1);
    }, { passive: true });
    vp.addEventListener("pointercancel", () => { sx = sy = null; }, { passive: true });

    /* The merchant's own pictures (design.gallery, in the order set in the Lab). An empty list brings the
       page's own product pictures back. On Shopify the same list is rendered by jk26-hero.liquid. */
    const original = { vp: vp.innerHTML, thumbs: thumbsBox ? thumbsBox.innerHTML : "" };
    const defaults = imgs().map((im) => im.getAttribute("src")).filter(Boolean);   // the page's own product pictures: what a gallery of the merchant's own starts from
    const title = ($("[data-jk26-product-title]") || {}).textContent || "Produktbilde";
    const attr = (v) => String(v).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
    function set(list) {
      const items = Array.isArray(list) ? list.filter((g) => g && g.src) : [];
      const sig = items.map((g) => g.src).join("|");
      if ((root.dataset.jk26Own || "") === sig) return;
      if (!items.length) { vp.innerHTML = original.vp; if (thumbsBox) thumbsBox.innerHTML = original.thumbs; delete root.dataset.jk26Own; show(0); return; }
      root.dataset.jk26Own = sig;
      vp.innerHTML = items.map((g, k) => `<img class="jk26-gallery__img" src="${attr(resolveImageUrl(g.src))}" alt="${attr(title.trim())}${k ? ", bilde " + (k + 1) : ""}" ${k ? 'loading="lazy"' : 'fetchpriority="high" loading="eager"'} decoding="async" data-jk26-gallery-img="${k}">`).join("");
      if (thumbsBox) thumbsBox.innerHTML = items.length > 1 ? items.map((g, k) => `<button class="jk26-thumb" role="tab" aria-selected="false" type="button" data-jk26-thumb="${k}"><img src="${attr(resolveImageUrl(g.src))}" width="88" height="88" alt="" loading="lazy" decoding="async"><span class="jk26-sr-only">Bilde ${k + 1}</span></button>`).join("") : "";
      show(Math.min(idx, items.length - 1));
    }
    JK26.gallery = { show, set, defaults, next: () => show(idx + 1), prev: () => show(idx - 1), get index() { return idx; }, get count() { return imgs().length; } };
    show(0);
    if (JK26.design && JK26.design.current) set(JK26.design.current.gallery);
  }

  /* ======================================================================
     SNOWFALL — one canvas per blue section, paused offscreen / hidden tab
     ====================================================================== */
  function initSnow() {
    if (motion === "off") { $$("[data-jk26-snow]").forEach((c) => c.remove()); return; }
    $$("[data-jk26-snow]").forEach((canvas) => {
      const ctx = canvas.getContext("2d", { alpha: true });
      if (!ctx) return;
      const host = canvas.parentElement;
      let w = 0, h = 0, flakes = [], raf = 0, running = false, visible = false;
      const density = (motion === "reduced" ? .45 : 1) * (window.innerWidth < 768 ? .55 : 1);
      function resize() {
        const r = host.getBoundingClientRect();
        w = Math.max(1, Math.round(r.width)); h = Math.max(1, Math.round(r.height));
        canvas.width = Math.round(w * DPR); canvas.height = Math.round(h * DPR);
        ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
        const n = Math.round(Math.min(140, (w * h) / 12000) * density);
        flakes = Array.from({ length: n }, () => spawn(true));
      }
      function spawn(anywhere) {
        const r = .8 + Math.random() * 2.4;
        return { x: Math.random() * w, y: anywhere ? Math.random() * h : -10, r, vy: .25 + r * .28 * (motion === "reduced" ? .6 : 1), vx: (Math.random() - .5) * .35, a: .25 + Math.random() * .6, ph: Math.random() * Math.PI * 2, sw: .4 + Math.random() * .8 };
      }
      function frame(t) {
        if (!running) return;
        ctx.clearRect(0, 0, w, h); ctx.fillStyle = "#fff";
        for (const f of flakes) {
          f.y += f.vy; f.x += f.vx + Math.sin(t / 1400 + f.ph) * f.sw * .35;
          if (f.y > h + 8) Object.assign(f, spawn(false));
          if (f.x > w + 8) f.x = -8; else if (f.x < -8) f.x = w + 8;
          ctx.globalAlpha = f.a; ctx.beginPath(); ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2); ctx.fill();
        }
        ctx.globalAlpha = 1;
        raf = requestAnimationFrame(frame);
      }
      function start() { if (running || !visible || document.hidden) return; running = true; raf = requestAnimationFrame(frame); }
      function stop() { running = false; cancelAnimationFrame(raf); }
      new ResizeObserver(() => resize()).observe(host);
      new IntersectionObserver((es) => { visible = es[0].isIntersecting; visible ? start() : stop(); }, { rootMargin: "80px" }).observe(host);
      document.addEventListener("visibilitychange", () => (document.hidden ? stop() : start()));
      resize();
    });
  }

  /* ======================================================================
     ERASE SURFACE — real freeform erasing with pointer events (shared engine)
     host: the element the canvases fill · paint(ctx, w, h): draws the cover
     ====================================================================== */
  function createEraseSurface(o) {
    const { host, canvas, fx, paint } = o;
    const completeAt = o.completeAt || .6, brushRatio = o.brushRatio || .095, minBrush = o.minBrush || 26, maxBrush = o.maxBrush || 96;
    const sparkles = o.sparkles !== false && motion !== "off";
    const ctx = canvas.getContext("2d");
    const fctx = fx ? fx.getContext("2d") : null;
    const probe = document.createElement("canvas"); probe.width = 48; probe.height = 48;
    const pctx = probe.getContext("2d", { willReadFrequently: true });
    const st = { w: 0, h: 0, strokes: [], down: false, last: null, pointerId: null, progress: 0, completed: false, moves: 0, particles: [], fxRaf: 0, enabled: true };

    const brushPx = () => Math.max(minBrush, Math.min(maxBrush, st.w * brushRatio));
    function paintCover() { ctx.setTransform(DPR, 0, 0, DPR, 0, 0); ctx.globalCompositeOperation = "source-over"; ctx.clearRect(0, 0, st.w, st.h); paint(ctx, st.w, st.h); }
    function stamp(x, y, r) { const g = ctx.createRadialGradient(x, y, r * .6, x, y, r); g.addColorStop(0, "rgba(0,0,0,1)"); g.addColorStop(1, "rgba(0,0,0,0)"); ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill(); }
    function erase(x0, y0, x1, y1) {
      ctx.globalCompositeOperation = "destination-out";
      const r = brushPx() / 2, d = Math.hypot(x1 - x0, y1 - y0), steps = Math.max(1, Math.ceil(d / (r * .35)));
      for (let i = 0; i <= steps; i++) { const t = i / steps; stamp(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t, r); }
      ctx.globalCompositeOperation = "source-over";
    }
    function replay() { paintCover(); for (const s of st.strokes) erase(s[0] * st.w, s[1] * st.h, s[2] * st.w, s[3] * st.h); }
    function measure() {
      pctx.clearRect(0, 0, 48, 48); pctx.drawImage(canvas, 0, 0, 48, 48);
      const d = pctx.getImageData(0, 0, 48, 48).data; let clear = 0;
      for (let i = 3; i < d.length; i += 4) if (d[i] < 150) clear++;
      st.progress = clear / (d.length / 4);
      o.onProgress && o.onProgress(st.progress);
      if (!st.completed && st.progress >= completeAt) complete("erased");
    }
    function complete(reason) {
      if (st.completed) return;
      st.completed = true; st.progress = 1; st.down = false;
      host.classList.add("is-done");
      o.onProgress && o.onProgress(1);
      o.onComplete && o.onComplete(reason);
    }
    function reset() {
      st.completed = false; st.strokes = []; st.progress = 0; st.particles = []; st.down = false;
      host.classList.remove("is-done");
      if (fctx) fctx.clearRect(0, 0, st.w, st.h);
      paintCover();
      o.onProgress && o.onProgress(0);
      o.onReset && o.onReset();
    }
    function resize() {
      /* the canvas's own layout size: not getBoundingClientRect, which shrinks while an entrance animation scales
         the card (and a ResizeObserver never reports the transform ending), and includes the card's border */
      const w = Math.max(1, canvas.clientWidth || host.clientWidth), h = Math.max(1, canvas.clientHeight || host.clientHeight);
      if (w === st.w && h === st.h) return;
      st.w = w; st.h = h;
      canvas.width = Math.round(w * DPR); canvas.height = Math.round(h * DPR);
      if (fx) { fx.width = canvas.width; fx.height = canvas.height; fctx.setTransform(DPR, 0, 0, DPR, 0, 0); }
      replay();
    }
    function spark(x, y) {
      if (!sparkles || !fctx) return;
      for (let i = 0; i < 2; i++) st.particles.push({ x, y, vx: (Math.random() - .5) * 1.6, vy: -.6 - Math.random() * 1.2, life: 1, r: 1.2 + Math.random() * 2.2, c: Math.random() > .4 ? "#FFFFFF" : "#FFD21C" });
      if (!st.fxRaf) st.fxRaf = requestAnimationFrame(fxFrame);
    }
    function fxFrame() {
      fctx.clearRect(0, 0, st.w, st.h);
      const ps = st.particles;
      for (let i = ps.length - 1; i >= 0; i--) {
        const p = ps[i]; p.x += p.vx; p.y += p.vy; p.vy += .03; p.life -= .035;
        if (p.life <= 0) { ps.splice(i, 1); continue; }
        fctx.globalAlpha = p.life; fctx.fillStyle = p.c; fctx.beginPath(); fctx.arc(p.x, p.y, p.r * p.life, 0, Math.PI * 2); fctx.fill();
      }
      fctx.globalAlpha = 1;
      st.fxRaf = ps.length ? requestAnimationFrame(fxFrame) : 0;
    }
    /* finger/mouse -> canvas units, exact under any transform (an entrance animation, a scaled Lab canvas) */
    const pt = (e) => { const r = canvas.getBoundingClientRect(); return { x: (e.clientX - r.left) * (r.width ? st.w / r.width : 1), y: (e.clientY - r.top) * (r.height ? st.h / r.height : 1) }; };
    function begin(p) {
      st.engaged = true;
      erase(p.x, p.y, p.x, p.y);
      st.strokes.push([p.x / st.w, p.y / st.h, p.x / st.w, p.y / st.h]);
      spark(p.x, p.y);
      o.onStart && o.onStart();
    }
    /* one finger scratches in every direction and nothing else happens: the cover has touch-action: none, the
       pointer is captured, and the touch events are cancelled too (older iOS ignored touch-action) */
    function onDown(e) {
      if (st.completed || !st.enabled) return;
      if (e.pointerType === "mouse" && e.button !== 0) return;
      if (st.down && e.pointerId !== st.pointerId) return;                 // a second finger: ignored
      st.down = true; st.pointerId = e.pointerId; st.last = pt(e);
      try { canvas.setPointerCapture(e.pointerId); } catch { /* ignore */ }
      begin(st.last);
      e.preventDefault();
    }
    function onMove(e) {
      if (!st.down || e.pointerId !== st.pointerId || st.completed) return;
      const p = pt(e);
      const pts = typeof e.getCoalescedEvents === "function" ? e.getCoalescedEvents() : [e];
      let last = st.last;
      for (const ce of pts) { const q = pt(ce); erase(last.x, last.y, q.x, q.y); st.strokes.push([last.x / st.w, last.y / st.h, q.x / st.w, q.y / st.h]); last = q; }
      st.last = p; spark(p.x, p.y);
      if (++st.moves % 5 === 0) measure();
      e.preventDefault();
    }
    function onUp(e) {
      if (e.pointerId !== st.pointerId) return;
      st.down = false; st.pointerId = null; st.last = null;
      try { canvas.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      if (!st.completed) measure();
    }
    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.addEventListener("pointercancel", onUp);
    canvas.addEventListener("lostpointercapture", onUp);
    canvas.addEventListener("contextmenu", (e) => e.preventDefault());
    const holdStill = (e) => { if (st.enabled && !st.completed && e.cancelable) e.preventDefault(); };
    canvas.addEventListener("touchstart", holdStill, { passive: false });
    canvas.addEventListener("touchmove", holdStill, { passive: false });
    canvas.addEventListener("dragstart", (e) => e.preventDefault());
    new ResizeObserver(() => resize()).observe(host);
    resize();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { if (!st.completed) replay(); });
    return { reset, complete, measure, repaint: replay, get progress() { return st.progress; }, get completed() { return st.completed; }, get strokes() { return st.strokes; }, set enabled(v) { st.enabled = !!v; } };
  }

  /* ---- cover painters ------------------------------------------------- */
  function roundRect(c, x, y, w, h, r) { c.beginPath(); c.moveTo(x + r, y); c.arcTo(x + w, y, x + w, y + h, r); c.arcTo(x + w, y + h, x, y + h, r); c.arcTo(x, y + h, x, y, r); c.arcTo(x, y, x + w, y, r); c.closePath(); }
  const DISPLAY = '"Bricolage Grotesque", "Plus Jakarta Sans", system-ui, sans-serif';
  const TEXT = '"Plus Jakarta Sans", system-ui, sans-serif';

  function paintFrost(ctx, w, h) {
    ctx.fillStyle = "#F3F9FF"; ctx.fillRect(0, 0, w, h);
    const g1 = ctx.createRadialGradient(w * .22, h * .18, 0, w * .22, h * .18, w * .7); g1.addColorStop(0, "rgba(69,187,234,.22)"); g1.addColorStop(1, "rgba(69,187,234,0)"); ctx.fillStyle = g1; ctx.fillRect(0, 0, w, h);
    const g2 = ctx.createRadialGradient(w * .82, h * .85, 0, w * .82, h * .85, w * .6); g2.addColorStop(0, "rgba(10,80,154,.14)"); g2.addColorStop(1, "rgba(10,80,154,0)"); ctx.fillStyle = g2; ctx.fillRect(0, 0, w, h);
    let seed = 1337; const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
    for (let i = 0; i < 1400; i++) { const x = rnd() * w, y = rnd() * h, r = rnd() * 2.2 + .3; ctx.globalAlpha = .05 + rnd() * .1; ctx.fillStyle = rnd() > .5 ? "#fff" : "#B9E2F6"; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill(); }
    for (let i = 0; i < 26; i++) { const x = rnd() * w, y = rnd() * h, r = 4 + rnd() * 10; ctx.globalAlpha = .12 + rnd() * .12; ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill(); }
    ctx.globalAlpha = 1;
    ctx.strokeStyle = "rgba(217,39,46,.55)"; ctx.lineWidth = 2; roundRect(ctx, w * .06, h * .06, w * .88, h * .88, 18); ctx.stroke();
    ctx.strokeStyle = "rgba(255,210,28,.9)"; ctx.lineWidth = 3; roundRect(ctx, w * .075, h * .075, w * .85, h * .85, 14); ctx.stroke();
    ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillStyle = "#14365B";
    const title = "Se baksiden av julekalenderen"; let size = Math.round(w * .062);
    ctx.font = `800 ${size}px ${DISPLAY}`;
    while (ctx.measureText(title).width > w * .76 && size > 14) { size -= 1; ctx.font = `800 ${size}px ${DISPLAY}`; }
    ctx.fillText(title, w / 2, h * .44);
    ctx.fillStyle = "#4A6283"; ctx.font = `600 ${Math.round(Math.max(13, w * .028))}px ${TEXT}`;
    ctx.fillText("Dra over bildet for å avdekke produktbildet", w / 2, h * .44 + size * .95);
    ctx.beginPath(); ctx.arc(w / 2, h * .62, Math.max(14, w * .035), 0, Math.PI * 2); ctx.strokeStyle = "rgba(20,54,91,.35)"; ctx.lineWidth = 1.5; ctx.stroke();
    ctx.beginPath(); ctx.arc(w / 2, h * .62, 3, 0, Math.PI * 2); ctx.fillStyle = "#14365B"; ctx.fill();
  }

  const WRAPS = {
    red:    { paper: "#D9272E", paper2: "#B81F26", ribbon: "#FFD21C", ribbon2: "#E9B800", ink: "#14365B", pattern: "dots" },
    blue:   { paper: "#0A509A", paper2: "#083B74", ribbon: "#FFD21C", ribbon2: "#E9B800", ink: "#14365B", pattern: "stripes" },
    green:  { paper: "#138C47", paper2: "#0E6B36", ribbon: "#FFFFFF", ribbon2: "#E6EEF5", ink: "#14365B", pattern: "dots" },
    yellow: { paper: "#FFD21C", paper2: "#E9B800", ribbon: "#D9272E", ribbon2: "#B81F26", ink: "#14365B", pattern: "stripes" },
    ice:    { paper: "#45BBEA", paper2: "#2EA3D6", ribbon: "#FFFFFF", ribbon2: "#E6EEF5", ink: "#14365B", pattern: "snow" },
    navy:   { paper: "#14365B", paper2: "#0B2240", ribbon: "#FFD21C", ribbon2: "#E9B800", ink: "#14365B", pattern: "snow" },
  };
  function paintGift(wrapKey, hint) {
    const c = WRAPS[wrapKey] || WRAPS.red;
    return function (ctx, w, h) {
      /* paper */
      const g = ctx.createLinearGradient(0, 0, w, h); g.addColorStop(0, c.paper); g.addColorStop(1, c.paper2);
      ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
      ctx.globalAlpha = .16; ctx.fillStyle = "#fff"; ctx.strokeStyle = "#fff";
      const step = Math.max(14, w * .085);
      if (c.pattern === "dots") { for (let y = step / 2; y < h; y += step) for (let x = ((y / step) % 2 ? step / 2 : 0) + step / 2; x < w; x += step) { ctx.beginPath(); ctx.arc(x, y, step * .13, 0, Math.PI * 2); ctx.fill(); } }
      else if (c.pattern === "stripes") { ctx.lineWidth = step * .22; for (let x = -h; x < w + h; x += step) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x + h, h); ctx.stroke(); } }
      else { for (let y = step / 2; y < h; y += step) for (let x = step / 2; x < w; x += step) { ctx.lineWidth = 1.2; for (let k = 0; k < 3; k++) { const a = (k * Math.PI) / 3; ctx.beginPath(); ctx.moveTo(x - Math.cos(a) * step * .2, y - Math.sin(a) * step * .2); ctx.lineTo(x + Math.cos(a) * step * .2, y + Math.sin(a) * step * .2); ctx.stroke(); } } }
      ctx.globalAlpha = 1;
      /* ribbons */
      const rw = Math.max(18, w * .15);
      const rib = (x, y, rw2, rh2) => { ctx.fillStyle = c.ribbon; ctx.fillRect(x, y, rw2, rh2); ctx.fillStyle = c.ribbon2; ctx.globalAlpha = .55; if (rw2 > rh2) ctx.fillRect(x, y + rh2 * .72, rw2, rh2 * .28); else ctx.fillRect(x + rw2 * .72, y, rw2 * .28, rh2); ctx.globalAlpha = 1; };
      rib(w / 2 - rw / 2, 0, rw, h); rib(0, h / 2 - rw / 2, w, rw);
      /* bow */
      const cx = w / 2, cy = h / 2, L = rw * 1.25;
      ctx.fillStyle = c.ribbon; ctx.strokeStyle = c.ribbon2; ctx.lineWidth = 1.5;
      for (const s of [-1, 1]) {
        ctx.beginPath(); ctx.ellipse(cx + s * L * .72, cy - L * .18, L * .68, L * .4, s * -.5, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(cx + s * L * .55, cy + L * .95); ctx.lineTo(cx + s * L * .22, cy + L * .78); ctx.closePath(); ctx.fill(); ctx.stroke();
      }
      ctx.beginPath(); ctx.arc(cx, cy, L * .26, 0, Math.PI * 2); ctx.fillStyle = c.ribbon2; ctx.fill();
      /* hint tag */
      if (hint) {
        ctx.font = `700 ${Math.max(11, Math.round(w * .052))}px ${TEXT}`;
        const tw = ctx.measureText(hint).width + w * .12, th = Math.max(26, w * .1);
        roundRect(ctx, cx - tw / 2, h * .82 - th / 2, tw, th, th / 2); ctx.fillStyle = "rgba(255,255,255,.94)"; ctx.fill();
        ctx.fillStyle = c.ink; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText(hint, cx, h * .82 + 1);
      }
    };
  }

  /* ---- 2a. frost card (optional section "Avdekk produktet") ------------- */
  function initReveal() {
    const root = $("[data-jk26-scratch]");
    if (!root) return;
    const media = $(".jk26-revealcard__media", root);
    const canvas = $("[data-jk26-scratch-canvas]", root);
    if (!media || !canvas) return;
    const back = $(".jk26-revealcard__img--back", root), front = $(".jk26-revealcard__img--front", root);
    const done = $("[data-jk26-scratch-done]", root), progressEl = $("[data-jk26-scratch-progress]", root);
    const seg = $("[data-jk26-side-toggle]", root), lead = $("[data-jk26-reveal-lead]");
    const opts = Object.assign({ enabled: true, completeAt: .6, sparkles: true }, cfg.scratch || {});
    if (root.dataset.jk26ScratchEnabled) opts.enabled = root.dataset.jk26ScratchEnabled === "true";
    if (root.dataset.jk26CompleteAt) opts.completeAt = Math.min(.9, Math.max(.3, Number(root.dataset.jk26CompleteAt) / 100)) || opts.completeAt;
    const surface = createEraseSurface({
      host: media, canvas, fx: $("[data-jk26-scratch-fx]", root), paint: paintFrost, completeAt: opts.completeAt, sparkles: opts.sparkles,
      onProgress: (p) => { if (progressEl) progressEl.style.setProperty("--p", p.toFixed(3)); },
      onComplete: (reason) => { root.classList.add("is-done"); if (done) done.hidden = false; if (lead) lead.textContent = "Baksiden av kalenderen er avdekket. Du kan nullstille og prøve igjen."; document.dispatchEvent(new CustomEvent("jk26:scratch-complete", { detail: { reason } })); },
      onReset: () => { root.classList.remove("is-done"); if (done) done.hidden = true; if (lead) lead.textContent = "Dra over bildet med musepekeren eller fingeren for å avdekke baksiden av kalenderen."; },
    });
    const showBtn = $("[data-jk26-scratch-show]", root), resetBtn = $("[data-jk26-scratch-reset]", root);
    showBtn && showBtn.addEventListener("click", () => surface.complete("button"));
    resetBtn && resetBtn.addEventListener("click", () => surface.reset());
    function setMode(mode) {
      root.dataset.mode = mode;
      if (mode === "toggle") { if (seg) seg.hidden = false; if (front) front.hidden = false; if (back) back.hidden = true; if (lead) lead.textContent = "Velg forside eller bakside for å se kalenderen fra begge sider."; if (done) done.hidden = true; surface.enabled = false; }
      else { if (seg) seg.hidden = true; if (front) front.hidden = true; if (back) back.hidden = false; surface.enabled = true; surface.reset(); }
    }
    if (seg) $$("[data-jk26-side]", seg).forEach((b) => b.addEventListener("click", () => {
      const side = b.dataset.jk26Side;
      $$("[data-jk26-side]", seg).forEach((x) => { const on = x === b; x.classList.toggle("is-active", on); x.setAttribute("aria-selected", on ? "true" : "false"); });
      if (front) front.hidden = side !== "front"; if (back) back.hidden = side !== "back";
    }));
    setMode(opts.enabled ? "scratch" : "toggle");
    JK26.scratch = { get progress() { return surface.progress; }, get completed() { return surface.completed; }, get strokes() { return surface.strokes; }, reset: () => surface.reset(), complete: (r) => surface.complete(r), setMode };
  }

  /* ---- 2b. five wrapped presents → product reveal ------------------------ */
  function initPresents() {
    const root = $("[data-jk26-presents]");
    if (!root) return;
    const opts = Object.assign({ enabled: true, completeAt: .55, sparkles: true }, cfg.scratch || {});
    if (root.dataset.jk26PresentsMode) opts.enabled = root.dataset.jk26PresentsMode !== "static";
    if (root.dataset.jk26CompleteAt) opts.completeAt = Math.min(.9, Math.max(.3, Number(root.dataset.jk26CompleteAt) / 100)) || opts.completeAt;
    const items = $$("[data-jk26-present]", root);
    const hint = root.dataset.jk26Hint || "Dra for å åpne";
    const surfaces = [];
    let opened = 0;
    const scope = root.closest("section") || document;          // controls + counter live in the section header
    const controls = $(".jk26-presents__controls", scope);
    const counter = $("[data-jk26-presents-count]", scope);
    const updateCount = () => { opened = items.filter((li) => li.classList.contains("is-open")).length; if (counter) counter.textContent = `${opened} av ${items.length} åpnet`; };
    const isCarousel = () => getComputedStyle(root).display === "flex";
    /* phones and tablets: the gifts are one sideways row that ONLY the arrows below move (the row is not
       swipeable: a finger on a gift scratches, in any direction) and NOTHING moves by itself. Opening a gift never
       scrolls the row or the page, and an opened gift stays open when the visitor goes back. Once every gift in
       view is open and more are waiting, the "next" arrow gets a gentle nudge, nothing more. */
    const nav = document.createElement("div");
    nav.className = "jk26-presents__nav";
    nav.innerHTML = '<button class="jk26-navbtn" type="button" data-jk26-presents-prev aria-label="Forrige gaver"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg></button><span class="jk26-presents__pos" aria-live="polite"></span><button class="jk26-navbtn" type="button" data-jk26-presents-next aria-label="Neste gaver"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg></button>';
    root.after(nav);
    const navPrev = $("[data-jk26-presents-prev]", nav), navNext = $("[data-jk26-presents-next]", nav), navPos = $(".jk26-presents__pos", nav);
    const ordered = () => $$("[data-jk26-present]", root);
    const padL = () => parseFloat(getComputedStyle(root).paddingLeft) || 0;
    const xOf = (li) => li.getBoundingClientRect().left - root.getBoundingClientRect().left - padL() + root.scrollLeft;   // place in the row
    function inView() {
      const o = ordered(); if (!o.length) return { first: 0, per: 1, n: 0 };
      let first = 0, best = Infinity;
      o.forEach((li, i) => { const d = Math.abs(xOf(li) - root.scrollLeft); if (d < best) { best = d; first = i; } });
      const w = o[0].getBoundingClientRect().width, gap = parseFloat(getComputedStyle(root).columnGap) || 0;
      const room = root.clientWidth - padL() - (parseFloat(getComputedStyle(root).paddingRight) || 0);
      return { first, per: Math.max(1, Math.floor((room + gap + 1) / (w + gap))), n: o.length };
    }
    function updateNav() {
      if (!isCarousel()) { nav.hidden = true; return; }
      nav.hidden = false;
      const { first, per, n } = inView();
      const last = Math.min(n, first + per);
      navPos.textContent = per > 1 ? `${first + 1}–${last} av ${n}` : `${first + 1} av ${n}`;
      navPrev.disabled = first <= 0;
      navNext.disabled = last >= n;
      const allOpen = ordered().slice(first, last).every((li) => li.classList.contains("is-open"));
      nav.classList.toggle("is-nudge", allOpen && !navNext.disabled);
    }
    function go(dir) {
      const o = ordered(); const { first, per, n } = inView();
      const target = Math.max(0, Math.min(Math.max(0, n - per), first + dir * per));
      if (o[target]) root.scrollTo({ left: xOf(o[target]), behavior: motion === "off" ? "auto" : "smooth" });
    }
    navPrev.addEventListener("click", () => go(-1));
    navNext.addEventListener("click", () => go(1));
    let navRaf = 0;
    root.addEventListener("scroll", () => { if (!navRaf) navRaf = requestAnimationFrame(() => { navRaf = 0; updateNav(); }); }, { passive: true });
    new ResizeObserver(updateNav).observe(root);

    items.forEach((li, i) => {
      const media = $(".jk26-present__media", li), canvas = $("[data-jk26-present-cover]", li), fx = $(".jk26-present__fx", li);
      const openBtn = $("[data-jk26-present-open]", li);
      const wrap = li.dataset.wrap || ["red", "blue", "green", "yellow", "ice"][i % 5];
      let surface = null;
      const open = (reason) => {
        if (li.classList.contains("is-open")) return;
        li.classList.add("is-open");
        li.setAttribute("aria-expanded", "true");
        if (openBtn) openBtn.hidden = true;
        if (canvas) canvas.setAttribute("aria-label", li.hasAttribute("data-jk26-mystery") ? "Gaven er åpnet: resten holder vi hemmelig." : "Gaven er åpnet.");
        updateCount();
        updateNav();
        document.dispatchEvent(new CustomEvent("jk26:present-open", { detail: { index: i, key: li.dataset.jk26PresentKey, reason } }));
      };
      const close = () => { li.classList.remove("is-open"); li.setAttribute("aria-expanded", "false"); if (openBtn) openBtn.hidden = false; if (canvas) canvas.setAttribute("aria-label", `Innpakket gave ${i + 1}. Dra over for å åpne.`); };
      if (media && canvas) {
        surface = createEraseSurface({ host: media, canvas, fx, paint: paintGift(wrap, hint), completeAt: opts.completeAt, brushRatio: .13, minBrush: 22, maxBrush: 60, sparkles: opts.sparkles, onComplete: open, onReset: () => { close(); updateNav(); } });
      }
      openBtn && openBtn.addEventListener("click", () => (surface ? surface.complete("button") : open("button")));
      surfaces.push({ li, surface, open, close });
    });

    function setMode(mode) {
      root.dataset.mode = mode;
      if (controls) controls.hidden = mode === "static";
      if (mode === "static") { surfaces.forEach((s) => { if (s.surface) s.surface.enabled = false; s.open("static"); }); }
      else { surfaces.forEach((s) => { if (s.surface) { s.surface.enabled = true; s.surface.reset(); } else s.close(); }); }
      updateCount();
    }
    /* there is no "open all" button on the page: a gift opens by dragging over it (keyboard users get the
       focus-only button on each gift). openAll() stays as a function for tests and the theme editor. */
    const openAll = $("[data-jk26-presents-open-all]", scope), resetAll = $("[data-jk26-presents-reset]", scope);
    const doOpenAll = () => surfaces.forEach((s, k) => setTimeout(() => (s.surface ? s.surface.complete("all") : s.open("all")), k * 120));
    openAll && openAll.addEventListener("click", doOpenAll);
    resetAll && resetAll.addEventListener("click", () => surfaces.forEach((s) => (s.surface ? s.surface.reset() : s.close())));
    /* order from the Lab: the five products can be reordered; the hidden gift stays last */
    function setOrder(order) {
      const byKey = new Map(items.map((li) => [li.dataset.jk26PresentKey, li]));
      const mystery = items.filter((li) => li.hasAttribute("data-jk26-mystery"));
      const wanted = (Array.isArray(order) ? order : []).map((k) => byKey.get(k)).filter((li) => li && !li.hasAttribute("data-jk26-mystery"));
      for (const li of items) if (!wanted.includes(li) && !li.hasAttribute("data-jk26-mystery")) wanted.push(li);
      [...wanted, ...mystery].forEach((li, k) => { root.appendChild(li); li.style.setProperty("--d", k * 70 + "ms"); });
    }
    setMode(opts.enabled ? "erase" : "static");
    updateNav();
    JK26.presents = { surfaces, setMode, setOrder, openAll: doOpenAll, reset: () => resetAll && resetAll.click(), get opened() { return items.filter((li) => li.classList.contains("is-open")).length; }, get order() { return $$("[data-jk26-present]", root).map((li) => li.dataset.jk26PresentKey); }, next: () => go(1), prev: () => go(-1), get view() { return inView(); } };
    if (JK26.design && JK26.design.current && JK26.design.current.presents && JK26.design.current.presents.order) setOrder(JK26.design.current.presents.order);
  }

  /* ======================================================================
     CAROUSELS (reviews, videos, presents on phones)
     ====================================================================== */
  function initCarousels() {
    $$("[data-jk26-carousel]").forEach((root) => {
      const track = $("[data-jk26-carousel-track]", root);
      const section = root.closest("section");
      const prev = section && $("[data-jk26-carousel-prev]", section);
      const next = section && $("[data-jk26-carousel-next]", section);
      if (!track) return;
      track.tabIndex = 0;
      const step = () => { const li = track.querySelector("li"); return li ? li.getBoundingClientRect().width + 18 : track.clientWidth * .8; };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      };
      prev && prev.addEventListener("click", () => track.scrollBy({ left: -step(), behavior: motion === "off" ? "auto" : "smooth" }));
      next && next.addEventListener("click", () => track.scrollBy({ left: step(), behavior: motion === "off" ? "auto" : "smooth" }));
      track.addEventListener("keydown", (e) => { if (e.key === "ArrowRight") { e.preventDefault(); next && next.click(); } if (e.key === "ArrowLeft") { e.preventDefault(); prev && prev.click(); } });
      let raf = 0;
      track.addEventListener("scroll", () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; update(); }); }, { passive: true });
      new ResizeObserver(update).observe(track);
      update();
    });
  }

  /* ======================================================================
     TEXT-REVIEW FLOW — two rows drifting continuously; pauses on hover,
     touch (drag to scrub) and keyboard focus; static scroller without motion
     ====================================================================== */
  function initFlow() {
    $$("[data-jk26-flow]").forEach((flow) => {
      const rows = $$(".jk26-flow__row", flow);
      if (!rows.length) return;
      if (motion === "off") { html.dataset.jk26FlowStatic = "1"; return; }
      const states = [];
      rows.forEach((row, ri) => {
        const track = $(".jk26-flow__track", row);
        if (!track) return;
        const originals = track.children.length;
        if (!originals) return;
        Array.from(track.children).forEach((li) => { const c = li.cloneNode(true); c.setAttribute("aria-hidden", "true"); track.appendChild(c); });
        const st = { row, track, originals, offset: 0, speed: (ri % 2 ? 20 : 26) * (motion === "reduced" ? .6 : 1), width: 0, pause: new Set(), idleTimer: 0 };
        const measure = () => { const first = track.children[st.originals]; st.width = first ? first.offsetLeft : track.scrollWidth / 2; };
        new ResizeObserver(measure).observe(track);
        measure();
        if (ri) st.offset = -st.width / 3;
        row.tabIndex = 0;
        row.setAttribute("role", "group");
        row.setAttribute("aria-label", `Anmeldelser, rad ${ri + 1}. Bevegelsen pauser når raden har fokus; piltastene blar.`);
        const hold = (why) => st.pause.add(why);
        const release = (why) => st.pause.delete(why);
        row.addEventListener("mouseenter", () => hold("hover"));
        row.addEventListener("mouseleave", () => release("hover"));
        row.addEventListener("focusin", () => hold("focus"));
        row.addEventListener("focusout", () => release("focus"));
        row.addEventListener("keydown", (e) => { if (e.key === "ArrowRight") { st.offset -= 90; e.preventDefault(); } if (e.key === "ArrowLeft") { st.offset += 90; e.preventDefault(); } });
        row.addEventListener("pointerdown", (e) => {
          if (e.pointerType === "mouse" && e.button !== 0) return;
          hold("drag"); clearTimeout(st.idleTimer);
          const x0 = e.clientX, start = st.offset, pid = e.pointerId;
          let moved = false;
          const move = (ev) => { if (ev.pointerId !== pid) return; const dx = ev.clientX - x0; if (Math.abs(dx) > 6) { moved = true; try { row.setPointerCapture(pid); } catch { /* ignore */ } } st.offset = start + dx; };
          const up = (ev) => { if (ev.pointerId !== pid) return; row.removeEventListener("pointermove", move); row.removeEventListener("pointerup", up); row.removeEventListener("pointercancel", up); st.idleTimer = setTimeout(() => release("drag"), moved ? 2200 : 400); };
          row.addEventListener("pointermove", move);
          row.addEventListener("pointerup", up);
          row.addEventListener("pointercancel", up);
        });
        states.push(st);
      });
      let visible = false, last = 0, raf = 0;
      function frame(t) {
        if (!visible) { raf = 0; return; }
        const dt = Math.min(0.05, (t - last) / 1000); last = t;
        for (const st of states) {
          if (!st.pause.size) st.offset -= st.speed * dt;
          if (st.width > 0) { while (st.offset <= -st.width) st.offset += st.width; while (st.offset > 0) st.offset -= st.width; }
          st.track.style.transform = `translate3d(${st.offset.toFixed(2)}px,0,0)`;
        }
        raf = requestAnimationFrame(frame);
      }
      const start = () => { if (!raf && visible && !document.hidden) { last = performance.now(); raf = requestAnimationFrame(frame); } };
      new IntersectionObserver((es) => { visible = es[0].isIntersecting; if (visible) start(); }, { rootMargin: "120px" }).observe(flow);
      document.addEventListener("visibilitychange", () => { if (!document.hidden) start(); });
      JK26.flow = { states };
    });
  }

  /* ======================================================================
     PHOTO-REVIEW TRIO — three cards per group, star navigation, blur/fade
     ====================================================================== */
  function initTrio() {
    JK26.trios = {};
    const phone = window.matchMedia ? window.matchMedia("(max-width: 767px)") : null;
    $$("[data-jk26-trio]").forEach((trio) => {
      const view = $("[data-jk26-trio-view]", trio);
      const items = view ? $$(":scope > li", view) : [];
      if (!items.length) return;
      const scope = trio.closest("section") || document;
      const wide = Math.max(1, Math.min(6, parseInt(trio.dataset.jk26TrioSize, 10) || 3));   // cards per group on wide screens: 3 for the photo reviews, 3–5 for the video wall
      const what = trio.classList.contains("jk26-trio--videos") ? "video" : "anmeldelse";
      let size = wide, groups = 1, g = 0, switching = false;
      const dots = document.createElement("div");
      dots.className = "jk26-trio__dots";
      dots.setAttribute("role", "tablist");
      dots.setAttribute("aria-label", what === "video" ? "Grupper med videoer" : "Grupper med anmeldelser");
      const prev = $("[data-jk26-carousel-prev]", scope), next = $("[data-jk26-carousel-next]", scope);
      function paint() {
        items.forEach((li, i) => { li.style.display = Math.floor(i / size) === g ? "" : "none"; });
        $$(".jk26-trio__dot", dots).forEach((b, i) => { b.classList.toggle("is-active", i === g); b.setAttribute("aria-selected", i === g ? "true" : "false"); });
        view.scrollLeft = 0;
        if (JK26.reviewMore) JK26.reviewMore();
      }
      /* a phone shows two side by side (the video wall: one or two, as set in the Lab), wider screens the group
         size set for the section; the first card in view stays in view when the screen changes between the two */
      function build() {
        const first = g * size;
        size = phone && phone.matches ? Math.min(Math.max(1, Math.min(2, parseInt(trio.dataset.jk26TrioSizeM, 10) || 2)), wide) : wide;
        groups = Math.ceil(items.length / size);
        g = Math.min(groups - 1, Math.floor(first / size));
        dots.innerHTML = "";
        for (let i = 0; i < groups; i++) {
          const b = document.createElement("button");
          b.type = "button"; b.className = "jk26-trio__dot"; b.setAttribute("role", "tab");
          b.setAttribute("aria-label", `Vis ${what} ${i * size + 1}–${Math.min(items.length, i * size + size)} av ${items.length}`);
          b.addEventListener("click", () => show(i));
          dots.appendChild(b);
        }
        if (groups > 1) { if (!dots.parentNode) trio.appendChild(dots); } else dots.remove();
        [prev, next].forEach((b) => { if (b) b.hidden = groups < 2; });
        paint();
      }
      function show(nextGroup, instant) {
        nextGroup = ((nextGroup % groups) + groups) % groups;
        if (nextGroup === g || switching) return;
        $$("video", trio).forEach((v) => { if (!v.paused) v.pause(); });   // a clip in the group that leaves stops
        if (instant || motion === "off") { g = nextGroup; paint(); return; }
        switching = true;
        trio.classList.add("is-switching");
        setTimeout(() => {
          g = nextGroup; paint();
          requestAnimationFrame(() => requestAnimationFrame(() => { trio.classList.remove("is-switching"); setTimeout(() => { switching = false; }, 330); }));
        }, prefersReduced ? 150 : 330);
      }
      prev && prev.addEventListener("click", () => show(g - 1));
      next && next.addEventListener("click", () => show(g + 1));
      /* a sideways swipe moves to the next or previous group (vertical scrolling stays the page's) */
      let sx = null, sy = 0;
      view.addEventListener("touchstart", (e) => { const t = e.touches[0]; sx = t.clientX; sy = t.clientY; }, { passive: true });
      view.addEventListener("touchend", (e) => {
        if (sx === null) return;
        const t = e.changedTouches[0], dx = t.clientX - sx, dy = t.clientY - sy; sx = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) show(g + (dx < 0 ? 1 : -1));
      }, { passive: true });
      build();
      if (phone && phone.addEventListener) phone.addEventListener("change", build);
      const api = { show, get group() { return g; }, get groups() { return groups; }, get size() { return size; } };
      JK26.trios[(scope.dataset && scope.dataset.jk26SectionKey) || "trio"] = api;
      if (what !== "video" && !JK26.trio) JK26.trio = api;   // the photo reviews keep their old name
    });
  }

  /* Long review texts are cut to a few lines on a phone (two cards side by side); «Les mer» shows the rest
     and the card grows. Checked whenever a group is shown, the screen changes or the fonts arrive. */
  function initReviewMore() {
    const phone = window.matchMedia ? window.matchMedia("(max-width: 767px)") : null;
    function check() {
      const on = !!(phone && phone.matches);
      $$(".jk26-review__text").forEach((t) => {
        const card = t.closest(".jk26-review"); if (!card) return;
        let btn = $(".jk26-review__more", card);
        if (card.classList.contains("is-expanded")) return;
        const cut = on && t.offsetParent !== null && t.scrollHeight > t.clientHeight + 2;
        if (cut && !btn) {
          btn = document.createElement("button");
          btn.type = "button"; btn.className = "jk26-review__more"; btn.textContent = "Les mer";
          btn.addEventListener("click", () => { card.classList.add("is-expanded"); btn.remove(); });
          t.after(btn);
        } else if (!cut && btn) btn.remove();
      });
    }
    JK26.reviewMore = () => requestAnimationFrame(check);
    if (phone && phone.addEventListener) phone.addEventListener("change", JK26.reviewMore);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(JK26.reviewMore);
    JK26.reviewMore();
  }

  /* ======================================================================
     VIDEO WALL — a clip loads and plays only after the visitor presses play;
     starting one pauses every other clip on the page
     ====================================================================== */
  function initVideoCards() {
    const cards = $$("[data-jk26-video]");
    const all = () => $$("[data-jk26-video] video, .jk26-cs__player video");
    cards.forEach((card) => {
      const video = $("video", card), btn = $(".jk26-videocard__play", card);
      if (!video || !btn) return;
      btn.addEventListener("click", () => {
        card.classList.add("is-started");
        video.controls = true;
        const p = video.play();
        if (p && p.catch) p.catch(() => { card.classList.remove("is-started"); video.controls = false; });   // could not start: the play button comes back
      });
      video.addEventListener("ended", () => { card.classList.remove("is-started"); video.controls = false; try { video.load(); } catch { /* ignore */ } });   // back to the cover picture
    });
    document.addEventListener("play", (e) => { const v = e.target; if (!(v instanceof HTMLVideoElement) || v.closest(".jk26-intro")) return; all().forEach((o) => { if (o !== v && !o.paused) o.pause(); }); }, true);
    if (cards.length && "IntersectionObserver" in window) {   // scrolled out of sight: stop
      const io = new IntersectionObserver((es) => es.forEach((en) => { if (!en.isIntersecting) $$("video", en.target).forEach((v) => { if (!v.paused) v.pause(); }); }), { threshold: 0 });
      $$('[data-jk26-section="videos"], .jk26-cs--video').forEach((s) => io.observe(s));
    }
  }

  /* ======================================================================
     ACCORDION
     ====================================================================== */
  function initAccordion() {
    $$("[data-jk26-accordion] .jk26-acc").forEach((item) => {
      const btn = $(".jk26-acc__btn", item);
      if (!btn) return;
      btn.addEventListener("click", () => {
        const open = !item.classList.contains("is-open");
        /* the folds under the buy box: one open at a time — opening one closes the one before (merchant, 2026-10-15) */
        const folds = open && item.closest(".jk26-folds");
        if (folds) $$(".jk26-acc.is-open", folds).forEach((o) => {
          if (o === item) return;
          o.classList.remove("is-open"); o.dataset.jk26Touched = "1";
          const b = $(".jk26-acc__btn", o); if (b) b.setAttribute("aria-expanded", "false");
        });
        item.classList.toggle("is-open", open); btn.setAttribute("aria-expanded", open ? "true" : "false"); item.dataset.jk26Touched = "1";
      });
    });
    const open = location.hash && location.hash.startsWith("#jk26-faq-") ? $(location.hash) : null;
    if (open) { const item = open.closest(".jk26-acc"); if (item) { item.classList.add("is-open"); $(".jk26-acc__btn", item).setAttribute("aria-expanded", "true"); } }
  }

  /* ======================================================================
     EXACT HEIGHT, LIVE CONTENT
     A section with an exact height (Lab; data-jk26-h in the preview, the export's height rule on Shopify — both mark
     it with --jk26-exact) is drawn as designed until its content needs more room than the design gave it: a fold-out
     panel a visitor opens, a phone that sets the text bigger. Then the section grows by exactly that much (--jk26-grow,
     part of both height rules) instead of letting the content run over the next section (the hero's folds over the red
     logo band on an iPhone, 2026-10-15), keeping up to 24 px of the room the design left below the content, and it is
     back at the designed height as soon as the content fits again. The content is what the merchant placed: the
     section's elements except the PNG decorations, measured where they are drawn (move and zoom included), so the
     section follows a panel while it opens. In the Lab's edit mode the exact height stays as set: shrinking a section
     below its content and then moving the elements must stay possible, and the overflow shows what to move.
     ====================================================================== */
  function initGrow() {
    const secs = $$("[data-jk26-section-key]");
    if (!secs.length || !("ResizeObserver" in window)) return;
    const state = new Map();   // section → { grow, room }
    const editing = () => html.classList.contains("jk26-lab-edit");
    const isExact = (sec) => getComputedStyle(sec).getPropertyValue("--jk26-exact").trim() === "1";
    const setGrow = (sec, s, px) => { s.grow = px; if (px) sec.style.setProperty("--jk26-grow", px + "px"); else sec.style.removeProperty("--jk26-grow"); };
    function measure(sec) {
      let s = state.get(sec);
      if (!s) { s = { grow: 0, room: null }; state.set(sec, s); }
      if (!isExact(sec) || editing()) { if (s.grow) setGrow(sec, s, 0); return; }
      const designedBottom = sec.getBoundingClientRect().bottom - s.grow;
      let bottom = -Infinity;
      $$("[data-jk26-layer]", sec).forEach((el) => {
        if (el.dataset.jk26Layer.startsWith("decor.")) return;
        const b = el.getBoundingClientRect();
        if (b.width > 0 && b.height > 0) bottom = Math.max(bottom, b.bottom);
      });
      if (bottom === -Infinity) return;
      if (s.room === null) {   // the room under the content as designed; content that never fitted keeps the section's own bottom padding
        const designed = Math.floor(designedBottom - bottom);
        s.room = Math.max(0, Math.min(24, designed >= 0 ? designed : parseFloat(getComputedStyle(sec).paddingBottom) || 0));
      }
      /* sub-pixel noise (positions are fractional and shift by hundredths when the page scrolls) is not a need for room */
      const need = bottom + s.room - designedBottom, grow = need > 0.5 ? Math.ceil(need) : 0;
      if (Math.abs(grow - s.grow) >= 1) setGrow(sec, s, grow);
    }
    let queued = false;
    const measureAll = () => { if (queued) return; queued = true; requestAnimationFrame(() => { queued = false; secs.forEach(measure); }); };
    const ro = new ResizeObserver(measureAll);
    secs.forEach((sec) => { ro.observe(sec); $$("[data-jk26-layer]", sec).forEach((el) => ro.observe(el)); });
    new MutationObserver(measureAll).observe(html, { attributes: true, attributeFilter: ["class"] });   // the Lab's edit / test mode
    document.addEventListener("jk26:design-applied", () => { state.forEach((s) => { s.room = null; }); measureAll(); });   // a new design: a new room
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measureAll);
    window.addEventListener("resize", measureAll);
    measureAll();
    JK26.grow = { measure: measureAll, of: (key) => { const sec = $(`[data-jk26-section-key="${key}"]`); const s = sec && state.get(sec); return s ? { grow: s.grow, room: s.room } : null; } };
  }

  /* ======================================================================
     STICKY BAR
     ====================================================================== */
  function initSticky() {
    const bar = $("[data-jk26-sticky]"), anchor = $("[data-jk26-buy]");
    if (!bar || !anchor) return;
    let past = false;
    /* the observer only reports crossings: a jump past a buy box that started below the fold (anchor link, End key,
       a long hero) is never one, so the place is also read on scroll, once per frame */
    const check = () => { const p = anchor.getBoundingClientRect().bottom < 0; if (p !== past) { past = p; render(); } };
    new IntersectionObserver(check, { threshold: 0 }).observe(anchor);
    let queued = false;
    addEventListener("scroll", () => { if (!queued) { queued = true; requestAnimationFrame(() => { queued = false; check(); }); } }, { passive: true });
    function render() {
      const show = past && html.classList.contains("jk26-revealed");
      bar.classList.toggle("is-visible", show);
      bar.setAttribute("aria-hidden", show ? "false" : "true");
      document.body.classList.toggle("jk26-has-sticky", show);
      if (show) html.style.setProperty("--jk26-sticky-h", bar.offsetHeight + "px");   // the shop's floating buttons move above the bar (jk26.css)
      $$("button, a", bar).forEach((el) => (el.tabIndex = show ? 0 : -1));
    }
    document.addEventListener("jk26:intro-complete", render);
    render();
  }

  /* ======================================================================
     SCROLL REVEALS (restrained: section groups only)
     ====================================================================== */
  function initReveals() {
    const els = $$("[data-jk26-reveal]");
    if (!els.length) return;
    if (motion === "off" || !("IntersectionObserver" in window)) { els.forEach((el) => el.classList.add("is-in")); return; }
    const io = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("is-in"); io.unobserve(e.target); } }), { threshold: .12, rootMargin: "0px 0px -6% 0px" });
    els.forEach((el) => io.observe(el));
  }

  /* ======================================================================
     PACKAGES — compact rows in the buy box (a radio group). The visitor picks
     one; every buy button on the page (hero, each section, the fixed bar)
     then adds all products of that package in one request, and the prices
     next to those buttons show the package total. The rows carry their
     variants and the formatted total (server: offers-lib, Shopify: liquid).
     ====================================================================== */
  const bundle = { id: null, items: null, price: null, name: null, qty: 1 };
  /* The packages: the picked row has a − n + stepper. n copies of a package cost n × its products at the live
     price minus what Shopify takes off for exactly n (data-jk26-save: one figure per quantity, measured in a
     throwaway cart or the store's observed rules — Shopify does not simply multiply a package discount). The buy
     button adds n × every product; Shopify's /cart/add increases what is already in the cart. */
  function initOffers() {
    const root = $("[data-jk26-offers]");
    if (!root) return;
    const stickyTitle = $(".jk26-sticky__title"), baseTitle = stickyTitle ? stickyTitle.textContent : "";
    const shipOver = Number(root.dataset.jk26ShipOver) || 0;
    if (!root.id) root.id = "jk26-velg-pakke";               // the one place every buy button below the hero leads to
    const err = $("[data-jk26-offers-error]", root);
    if (err) { err.setAttribute("role", "status"); err.setAttribute("aria-live", "polite"); }
    const explain = (text) => { if (err) { err.textContent = text; err.hidden = !text; } };
    const qtyMax = Math.max(1, Number(root.dataset.jk26QtyMax) || 1);
    if (root.dataset.jk26MoneyFormat && !cfg.moneyFormat) cfg.moneyFormat = root.dataset.jk26MoneyFormat;
    const qtyOf = new Map();   // each package keeps its own quantity while the visitor compares
    const savesOf = (row) => (row.dataset.jk26Save || "").split(",").filter((x) => x !== "").map(Number).filter((x) => Number.isFinite(x));
    const maxFor = (row) => Math.max(1, Math.min(qtyMax, savesOf(row).length || 1));
    function figures(row, n) {
      const one = Number(row.dataset.jk26Full) || 0, saves = savesOf(row);
      const save = saves.length ? saves[Math.min(n, saves.length) - 1] : 0;
      return { full: one * n, save, total: one * n - save };
    }
    function paintRow(row) {
      const n = qtyOf.get(row) || 1;
      const val = $("[data-jk26-qty-val]", row); if (val) val.textContent = String(n);
      const dec = $("[data-jk26-qty-dec]", row), inc = $("[data-jk26-qty-inc]", row);
      if (dec) dec.disabled = n <= 1;
      if (inc) inc.disabled = n >= maxFor(row);
      row.dataset.jk26Qty = String(n);
      if (!row.dataset.jk26Full || row.classList.contains("is-off")) return;   // no figures (sold out / older markup): the printed ones stay
      const f = figures(row, n), price = $(".jk26-bundle__price", row), tag = $(".jk26-bundle__tag", row);
      if (price) price.innerHTML = (f.save ? `<s>${formatMoney(f.full)}</s>` : "") + `<b>${formatMoney(f.total)}</b>` + (shipOver && f.total >= shipOver ? `<span class="jk26-bundle__ship">✓ Fri frakt</span>` : "");
      if (tag) { tag.textContent = f.save ? `Spar ${formatMoney(f.save)}` : "Standardpris"; tag.classList.toggle("jk26-bundle__tag--none", !f.save); }
      row.dataset.jk26OfferPrice = formatMoney(f.total);
    }
    function pick(row) {
      if (!row || row.classList.contains("is-off")) return;
      if (offersOpen()) explain("");
      $$(".jk26-bundle", root).forEach((r) => r.classList.toggle("is-selected", r === row));
      const radio = $(".jk26-bundle__radio", row); if (radio && !radio.checked) radio.checked = true;
      const n = qtyOf.get(row) || 1;
      bundle.id = row.dataset.jk26Offer;
      bundle.qty = n;
      bundle.items = (row.dataset.jk26OfferItems || "").split(",").map((p) => { const [id, qty] = p.split(":"); return { id: Number(id), quantity: (Number(qty) || 1) * n }; }).filter((it) => it.id > 0);
      bundle.price = row.dataset.jk26OfferPrice || null;
      const nameEl = $(".jk26-bundle__name", row);
      bundle.name = nameEl ? nameEl.textContent.trim() : row.dataset.jk26OfferName || null;
      if (bundle.price) $$("[data-jk26-price], [data-jk26-cta-price]").forEach((el) => { el.textContent = bundle.price; });
      const plain = bundle.name ? bundle.name.replace(/^1\s*×\s*/, "") : "";
      if (stickyTitle) stickyTitle.textContent = !bundle.name || (bundle.id === "solo" && n === 1) ? baseTitle : n > 1 ? `${n} × ${plain}` : bundle.name;
      root.dataset.jk26Selected = bundle.id;
    }
    function setQty(row, n) {
      if (!row || row.classList.contains("is-off")) return;
      qtyOf.set(row, Math.max(1, Math.min(maxFor(row), n)));
      paintRow(row);
      if (row.classList.contains("is-selected")) pick(row);   // the buy button, the fixed bar and every price follow
    }
    root.addEventListener("change", (e) => { const r = e.target.closest(".jk26-bundle__radio"); if (r) pick(r.closest(".jk26-bundle")); });
    root.addEventListener("click", (e) => {
      /* a package marked sold out (in the Lab, or because a product in it is): visible, never picked, and it says why */
      const offRow = e.target.closest(".jk26-bundle.is-off");
      if (offRow) {
        e.preventDefault();
        const nameEl = $(".jk26-bundle__name", offRow), st = $(".jk26-bundle__state", offRow);
        const name = (nameEl ? nameEl.textContent.trim() : offRow.dataset.jk26OfferName) || "Denne pakken";
        explain(`${name} er ${st && /ikke/i.test(st.textContent) ? "ikke tilgjengelig" : "utsolgt"}. Velg en annen pakke.`);
        return;
      }
      const b = e.target.closest("[data-jk26-qty-dec], [data-jk26-qty-inc]");
      if (!b) return;
      e.preventDefault();   // a button inside the row's label: it changes the quantity and nothing else
      const row = b.closest(".jk26-bundle");
      if (!b.disabled) setQty(row, (qtyOf.get(row) || 1) + (b.hasAttribute("data-jk26-qty-inc") ? 1 : -1));
    });
    /* COUNTDOWN (2026-10-18): a package with an end time (data-jk26-ends: an exact time with its offset, from the Lab's
       «Nedtelling») counts down on its row every second. At 0 the row becomes «Utsolgt» like any sold-out package: faded,
       not selectable, no stepper, nothing to add; if it was the picked one the first package still on sale takes over.
       Also run at the press of a buy button (sweep), so a package can never be added a second after its end. The page
       only: the Shopify discount behind the package is ended in admin. */
    const endsText = (ms) => { const s = Math.max(0, Math.ceil(ms / 1000)), d = Math.floor(s / 86400), p = (n) => String(n).padStart(2, "0"); return (d ? `${d} ${d === 1 ? "dag" : "dager"} ` : "") + `${p(Math.floor((s % 86400) / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`; };
    let endFmt = null;
    try { endFmt = new Intl.DateTimeFormat("nb-NO", { timeZone: "Europe/Oslo", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }); } catch { /* no title then */ }
    function endRow(row, quiet) {
      const was = row.classList.contains("is-selected");
      const nameEl = $(".jk26-bundle__name", row), name = (nameEl ? nameEl.textContent.trim() : row.dataset.jk26OfferName) || "Pakken";
      row.classList.add("is-off"); row.classList.remove("is-selected");
      row.removeAttribute("data-jk26-offer-items");
      row.dataset.jk26Ended = "1";
      const radio = $(".jk26-bundle__radio", row); if (radio) { radio.checked = false; radio.disabled = true; }
      const qrow = $("[data-jk26-qty-row]", row); if (qrow) qrow.remove();
      const badge = document.createElement("span"); badge.className = "jk26-bundle__state"; badge.textContent = "Utsolgt";
      const t = $("[data-jk26-ends]", row); if (t) t.replaceWith(badge);
      if (quiet) return;   // while the page starts: the start-up below picks the package and sets the buy button
      const next = $(".jk26-bundle:not(.is-off)", root);
      if (was && next) { pick(next); explain(`Tilbudet «${name}» er over.`); }
      else if (!next) { bundle.id = null; bundle.items = null; explain("Alle pakkene er utsolgt akkurat nå."); }
      setAvailability(product.state);
    }
    let ticker = 0;
    function sweep(quiet) {
      const now = Date.now();
      let running = 0;
      $$(".jk26-bundle [data-jk26-ends]", root).forEach((el) => {
        const end = Date.parse(el.dataset.jk26Ends);
        if (!Number.isFinite(end)) return;
        const row = el.closest(".jk26-bundle");
        if (now >= end) { endRow(row, quiet); return; }
        running++;
        if (endFmt && !el.title) el.title = "Tilbudet varer til " + endFmt.format(end);
        const out = $("[data-jk26-countdown]", el), txt = endsText(end - now);
        if (out && out.textContent !== txt) out.textContent = txt;
      });
      if (!running && ticker) { clearInterval(ticker); ticker = 0; }
      return running;
    }
    $$(".jk26-bundle", root).forEach(paintRow);
    if (sweep(true)) ticker = setInterval(() => sweep(false), 1000);   // a page drawn before the end (cached) is put right at once
    const checked = $(".jk26-bundle__radio:checked", root);
    const first = checked && !checked.disabled ? checked.closest(".jk26-bundle") : $(".jk26-bundle:not(.is-off)", root);
    if (first) pick(first);
    else { $$(".jk26-bundle__radio", root).forEach((r) => { r.checked = false; }); $$(".jk26-bundle", root).forEach((r) => r.classList.remove("is-selected")); explain("Alle pakkene er utsolgt akkurat nå."); }
    setAvailability(product.state);                           // the buy button follows: unusable while no package can be bought
    const rowOf = (id) => $(`.jk26-bundle[data-jk26-offer="${CSS.escape(id)}"]`, root);
    JK26.bundles = { pick: (id) => pick(rowOf(id)), setQty: (id, n) => setQty(rowOf(id), n), figures: (id, n) => figures(rowOf(id), n), sweep: () => sweep(false), get current() { return { ...bundle }; } };
  }

  /* ======================================================================
     PREVIEW-ONLY: demo cart drawer + header count
     ====================================================================== */
  function initPreviewCart() {
    if (!isPreview) return;
    const drawer = $("[data-jk26-pvcart]"), lines = $("[data-jk26-pvcart-lines]"), total = $("[data-jk26-pvcart-total]"), count = $("[data-jk26-cart-count]");
    if (!drawer) return;
    function render(cart) {
      if (count) count.textContent = String(cart.item_count || 0);
      if (!lines) return;
      lines.innerHTML = "";
      if (!cart.items || !cart.items.length) lines.innerHTML = '<p class="jk26-pvcart__empty">Handlekurven er tom.</p>';
      cart.items.forEach((it) => {
        const li = document.createElement("div"); li.className = "jk26-pvcart__line";
        li.innerHTML = `<img src="assets/product/front-600.webp" alt=""><div><strong></strong><span></span></div><span class="jk26-pvcart__lineprice"></span>`;
        $("img", li).src = it.image || "assets/product/front-600.webp";
        $("strong", li).textContent = it.product_title; $("div span", li).textContent = `Antall: ${it.quantity}`;
        const discounted = it.line_price !== undefined && it.final_line_price < it.line_price;
        $(".jk26-pvcart__lineprice", li).innerHTML = discounted ? `<s>${formatMoney(it.line_price)}</s> ${formatMoney(it.final_line_price)}` : formatMoney(it.final_line_price);
        lines.appendChild(li);
      });
      const disc = $("[data-jk26-pvcart-discount]");
      if (disc) { const d = cart.total_discount || 0; disc.hidden = !d; if (d) $("b", disc).textContent = "−" + formatMoney(d); }
      if (total) total.textContent = formatMoney(cart.total_price || 0);
    }
    const open = () => { drawer.hidden = false; $("[data-jk26-pvcart-close]", drawer).focus(); };
    const close = () => { drawer.hidden = true; };
    $$("[data-jk26-pvcart-close]", drawer).forEach((b) => b.addEventListener("click", close));
    document.addEventListener("keydown", (e) => { if (e.key === "Escape" && !drawer.hidden) close(); });
    const opener = $("[data-jk26-cart-open]");
    opener && opener.addEventListener("click", () => { fetch("cart.js").then((r) => r.json()).then(render).finally(open); });
    document.addEventListener("jk26:cart-added", (e) => { if (e.detail.cart) render(e.detail.cart); open(); });
    fetch("cart.js").then((r) => r.json()).then(render).catch(() => {});
  }

  /* ======================================================================
     BOOT
     ====================================================================== */
  function initDocLinks() {
    if (isPreview) return;   // the preview server serves /dokument/<doc> itself
    $$("a[data-jk26-doc]").forEach((a) => {
      try {
        const u = new URL(location.href);
        u.hash = ""; u.searchParams.delete("variant"); u.searchParams.set("view", "jk26-" + a.dataset.jk26Doc);
        a.href = u.pathname + u.search;
      } catch { /* the printed address stays */ }
    });
  }

  function boot() {
    applyFlags();
    initDocLinks();
    initDesign();
    initProduct();
    initOffers();
    initBuy();
    initGallery();
    initSnow();
    initReveal();
    initPresents();
    initCarousels();
    initFlow();
    initReviewMore();
    initTrio();
    initVideoCards();
    initAccordion();
    initGrow();
    initSticky();
    initReveals();
    initPreviewCart();
    JK26.ready = true;
    document.dispatchEvent(new CustomEvent("jk26:ready"));
    log("ready", cfg);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot); else boot();
})();
